import React, { useMemo, useState, useEffect } from 'react';
import 'reactflow/dist/style.css';
import ReactFlow, { Background, Controls, MiniMap, Handle, Position } from 'reactflow';
import API_ENDPOINTS from '../../Config/api';
import { fetchWithAuth } from '../../Utils/fetchWithAuth';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Circle, CheckCircle2, AlertTriangle, HelpCircle, Rocket, Search, Cloud, Globe,
  Signal, Monitor, Shield, Users, Tv, Gamepad2, Briefcase, Settings, Plus, Trash2,
  ScrollText, MailX, Lock, RefreshCw, Hourglass, Check, X, BarChart3, TrendingUp,
  Siren, Lightbulb, Radio, Upload, KeyRound, Zap, Home, Package, BookOpen, Building,
  ThumbsUp, Microscope, Dice5, Timer, Plug, MapPin, ClipboardList, MessageCircle,
  Bot, User, Mail, GraduationCap, Target, Wrench, Eye } from 'lucide-react';

// Small sparkline component (SVG)

// Custom node components with icons (transparent background to show edges)
const CustomNode = ({ data }) => {
  const { t } = useTranslation();
  const { label, iconSvg, ip, hasIncident } = data;
  const borderColor = hasIncident ? '#ef4444' : '#e5e7eb';
  const borderWidth = hasIncident ? '3px' : '1px';
  const bgColor = hasIncident ? '#fee2e2' : 'transparent';
  const textColor = hasIncident ? '#dc2626' : (data.color || '#000');
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px', borderRadius: '8px', background: bgColor, border: `${borderWidth} solid ${borderColor}`, boxShadow: hasIncident ? '0 0 12px rgba(239, 68, 68, 0.4)' : '0 2px 4px rgba(0,0,0,0.1)', color: textColor, fontWeight: 700, minWidth: '80px', minHeight: '60px', justifyContent: 'center' }}>
      <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
      {iconSvg && <div dangerouslySetInnerHTML={{ __html: iconSvg }} style={{ marginBottom: '4px' }} />}
      <span style={{ fontSize: '12px', textAlign: 'center' }}>{label}</span>
      {ip && <span style={{ fontSize: '9px', fontWeight: 400, color: hasIncident ? '#dc2626' : '#6b7280', marginTop: '2px' }}>{ip}</span>}
      {hasIncident && <span style={{ fontSize: '8px', fontWeight: 600, color: '#dc2626', marginTop: '2px', textTransform: 'uppercase' }}><AlertTriangle size={10} className="inline mr-0.5" />{t('networkingPage.incident')}</span>}
      <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
    </div>
  );
};


// Define nodeTypes as a plain object outside any component (no hooks)
const nodeTypes = {
  custom: CustomNode,
};

// Small sparkline component (SVG)
function Sparkline({ values = [], color = '#3b82f6', stroke = 2, width = 140, height = 40 }) {
  const points = useMemo(() => {
    if (!values || values.length === 0) return '';
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = Math.max(1, max - min);
    return values
      .map((v, i) => {
        const x = (i / (values.length - 1)) * width;
        const y = height - ((v - min) / range) * height;
        return `${x},${y}`;
      })
      .join(' ');
  }, [values, width, height]);

  if (!points) return null;
  return (
    <svg width={'100%'} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <polyline fill="none" stroke={color} strokeWidth={stroke} points={points} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Improved topology with React Flow: symbols and legend
function TopologyDiagram({ className, incidentIPs = [], topologyData = null }) {
  const { t } = useTranslation();
  // Memoize nodeTypes inside the component to avoid React Flow warning and ESLint error
  const nodeTypes = React.useMemo(() => ({ custom: CustomNode }), []);
  // Se ci sono dati reali dall'API, usali; altrimenti usa i dati statici SOLO se topologyData === 'mock'
  const hasRealData = topologyData && topologyData.nodes && topologyData.nodes.length > 0;
  const showMock = topologyData === 'mock';
  
  
  // Icon SVGs inspired by Packet Tracer
  const icons = {
    internet: `<svg width="40" height="30" viewBox="0 0 50 35"><path d="M12 25 Q12 12 25 12 Q38 12 38 25 Q38 30 32 33 Q25 35 18 33 Q12 30 12 25 Z" fill="currentColor"/><text x="25" y="22" textAnchor="middle" fontSize="10" fill="#fff">C</text></svg>`,
    router: `<svg width="40" height="30" viewBox="0 0 50 35"><rect x="8" y="15" width="34" height="12" fill="currentColor"/><line x1="12" y1="8" x2="12" y2="15" stroke="#fff" strokeWidth="2"/><line x1="25" y1="8" x2="25" y2="15" stroke="#fff" strokeWidth="2"/><line x1="38" y1="8" x2="38" y2="15" stroke="#fff" strokeWidth="2"/><circle cx="12" cy="21" r="1.5" fill="#fff"/><circle cx="25" cy="21" r="1.5" fill="#fff"/><circle cx="38" cy="21" r="1.5" fill="#fff"/></svg>`,
    switch: `<svg width="40" height="30" viewBox="0 0 50 35"><rect x="8" y="15" width="34" height="12" fill="currentColor"/><line x1="10" y1="8" x2="10" y2="15" stroke="#fff" strokeWidth="1"/><line x1="14" y1="8" x2="14" y2="15" stroke="#fff" strokeWidth="1"/><line x1="18" y1="8" x2="18" y2="15" stroke="#fff" strokeWidth="1"/><line x1="22" y1="8" x2="22" y2="15" stroke="#fff" strokeWidth="1"/><line x1="26" y1="8" x2="26" y2="15" stroke="#fff" strokeWidth="1"/><line x1="30" y1="8" x2="30" y2="15" stroke="#fff" strokeWidth="1"/><line x1="34" y1="8" x2="34" y2="15" stroke="#fff" strokeWidth="1"/><line x1="38" y1="8" x2="38" y2="15" stroke="#fff" strokeWidth="1"/></svg>`,
    ap: `<svg width="40" height="30" viewBox="0 0 50 35"><rect x="8" y="15" width="34" height="12" fill="currentColor"/><line x1="12" y1="8" x2="12" y2="15" stroke="#fff" strokeWidth="2"/><line x1="25" y1="8" x2="25" y2="15" stroke="#fff" strokeWidth="2"/><line x1="38" y1="8" x2="38" y2="15" stroke="#fff" strokeWidth="2"/><circle cx="12" cy="21" r="1.5" fill="#fff"/><circle cx="25" cy="21" r="1.5" fill="#fff"/><circle cx="38" cy="21" r="1.5" fill="#fff"/><path d="M8 8 Q18 3 28 8" stroke="#fff" strokeWidth="2" fill="none"/><path d="M11 11 Q21 6 31 11" stroke="#fff" strokeWidth="1.5" fill="none" opacity="0.7"/><path d="M14 14 Q24 9 34 14" stroke="#fff" strokeWidth="1" fill="none" opacity="0.5"/></svg>`,
    pc: `<svg width="30" height="30" viewBox="0 0 35 35"><rect x="6" y="6" width="23" height="17" fill="currentColor"/><rect x="9" y="10" width="17" height="11" fill="#fff"/><rect x="12" y="26" width="11" height="4" fill="currentColor"/><line x1="17" y1="30" x2="17" y2="35" stroke="currentColor" strokeWidth="2"/></svg>`,
    server: `<svg width="30" height="30" viewBox="0 0 35 35"><rect x="6" y="6" width="23" height="17" fill="currentColor"/><rect x="9" y="10" width="17" height="11" fill="#fff"/><rect x="12" y="26" width="11" height="4" fill="currentColor"/><line x1="17" y1="30" x2="17" y2="35" stroke="currentColor" strokeWidth="2"/></svg>`,
    printer: `<svg width="30" height="30" viewBox="0 0 35 35"><rect x="8" y="8" width="19" height="10" fill="currentColor" rx="2"/><rect x="6" y="18" width="23" height="8" fill="currentColor" rx="2"/><rect x="10" y="22" width="15" height="2" fill="#fff"/><rect x="12" y="12" width="11" height="4" fill="#fff" opacity="0.3"/><circle cx="9" cy="22" r="1.5" fill="#10b981"/></svg>`,
    phone: `<svg width="25" height="30" viewBox="0 0 30 35"><rect x="8" y="5" width="14" height="25" fill="currentColor" rx="2"/><rect x="10" y="8" width="10" height="16" fill="#fff"/><circle cx="15" cy="27" r="1.5" fill="#fff"/><line x1="12" y1="6" x2="18" y2="6" stroke="#fff" strokeWidth="1"/></svg>`,
    smartphone: `<svg width="25" height="30" viewBox="0 0 30 35"><rect x="8" y="5" width="14" height="25" fill="currentColor" rx="2"/><rect x="10" y="8" width="10" height="16" fill="#fff"/><circle cx="15" cy="27" r="1.5" fill="#fff"/><line x1="12" y1="6" x2="18" y2="6" stroke="#fff" strokeWidth="1"/></svg>`,
    device: `<svg width="30" height="30" viewBox="0 0 35 35"><rect x="6" y="6" width="23" height="17" fill="currentColor"/><rect x="9" y="10" width="17" height="11" fill="#fff"/><rect x="12" y="26" width="11" height="4" fill="currentColor"/><line x1="17" y1="30" x2="17" y2="35" stroke="currentColor" strokeWidth="2"/></svg>`
  };

  // Mappa tipo dispositivo -> icona e colore
  const deviceTypeMap = {
    internet: { icon: icons.internet, color: '#60a5fa' },
    router: { icon: icons.router, color: '#fb923c' },
    gateway: { icon: icons.router, color: '#fb923c' }, // Gateway usa stessa icona di router
    switch: { icon: icons.switch, color: '#10b981' },
    ap: { icon: icons.ap, color: '#7c3aed' },
    pc: { icon: icons.pc, color: '#60a5fa' },
    server: { icon: icons.server, color: '#10b981' },
    printer: { icon: icons.printer, color: '#8b5cf6' },
    phone: { icon: icons.phone, color: '#ec4899' },
    smartphone: { icon: icons.smartphone, color: '#ec4899' },
    device: { icon: icons.device, color: '#60a5fa' },
  };

  let nodes = [];
  let edges = [];

  // Se ci sono dati reali dall'API, convertili al formato React Flow
  if (hasRealData) {
    
    // Organizza nodi per tipo per layout gerarchico
    const nodesByType = {
      internet: [],
      router: [],
      gateway: [],
      switch: [],
      pc: [],
      printer: [],
      phone: [],
      ap: [],
      server: [],
      other: []
    };
    
    topologyData.nodes.forEach(node => {
      const type = node.type?.toLowerCase() || 'other';
      if (nodesByType[type]) {
        nodesByType[type].push(node);
      } else {
        nodesByType.other.push(node);
      }
    });
    
    // Converti nodi dall'API al formato React Flow con layout gerarchico ordinato
    nodes = [];
    const centerX = 600; // punto centrale per il layout
    const layerYStart = 80;
    const layerSpacing = 140;

    const placeLayer = (items, y, spacing = 180) => {
      if (!items || items.length === 0) return;
      const count = items.length;
      const totalWidth = (count - 1) * spacing;
      const startX = Math.max(120, Math.round(centerX - totalWidth / 2));
      items.forEach((node, idx) => {
        const typeKey = (node.type || node.device_type || '').toLowerCase();
        const mapKey = deviceTypeMap[typeKey] ? typeKey : (typeKey === 'pc' ? 'pc' : (typeKey === 'printer' ? 'printer' : 'device'));
        const deviceInfo = deviceTypeMap[mapKey] || deviceTypeMap.device;
        nodes.push({
          id: node.id,
          type: 'custom',
          data: {
            label: node.label || `${mapKey.charAt(0).toUpperCase() + mapKey.slice(1)} ${node.id}`,
            iconSvg: deviceInfo.icon,
            color: deviceInfo.color,
            ip: node.id,
            hasIncident: incidentIPs.includes(node.id)
          },
          position: { x: startX + idx * spacing, y }
        });
      });
    };

    // Layer order: internet, routers/gateways, switches, access points, end devices, servers
    let layerIdx = 0;
    placeLayer(nodesByType.internet, layerYStart + layerIdx * layerSpacing, 220); layerIdx++;
    // Combina router e gateway nello stesso layer
    const routersAndGateways = [...nodesByType.router, ...nodesByType.gateway];
    placeLayer(routersAndGateways, layerYStart + layerIdx * layerSpacing, 220); layerIdx++;
    placeLayer(nodesByType.switch, layerYStart + layerIdx * layerSpacing, 200); layerIdx++;
    placeLayer(nodesByType.ap, layerYStart + layerIdx * layerSpacing, 160); layerIdx++;

    // End devices: merge pc/printer/phone/other and place in grid of up to 6 per row
    const endDevices = [
      ...nodesByType.pc.map(n => ({ ...n, _type: 'pc' })),
      ...nodesByType.printer.map(n => ({ ...n, _type: 'printer' })),
      ...nodesByType.phone.map(n => ({ ...n, _type: 'phone' })),
      ...nodesByType.other.map(n => ({ ...n, _type: 'device' }))
    ];
    if (endDevices.length > 0) {
      const perRow = 6;
      const rowSpacingX = 140;
      const rowSpacingY = 110;
      const rows = Math.ceil(endDevices.length / perRow);
      for (let r = 0; r < rows; r++) {
        const slice = endDevices.slice(r * perRow, (r + 1) * perRow);
        const y = layerYStart + layerIdx * layerSpacing + r * rowSpacingY;
        const totalWidth = (slice.length - 1) * rowSpacingX;
        const startX = Math.max(120, Math.round(centerX - totalWidth / 2));
        slice.forEach((node, i) => {
          const mapKey = node._type || 'device';
          const deviceInfo = deviceTypeMap[mapKey] || deviceTypeMap.device;
          nodes.push({
            id: node.id,
            type: 'custom',
            data: {
              label: node.label || `${mapKey.charAt(0).toUpperCase() + mapKey.slice(1)} ${node.id}`,
              iconSvg: deviceInfo.icon,
              color: deviceInfo.color,
              ip: node.id,
              hasIncident: incidentIPs.includes(node.id)
            },
            position: { x: startX + i * rowSpacingX, y }
          });
        });
      }
      layerIdx += Math.ceil(endDevices.length / perRow);
    }

    // Servers: place on the right side vertically aligned
    if (nodesByType.server.length > 0) {
      const serverX = centerX + 480;
      const serverSpacingY = 120;
      nodesByType.server.forEach((node, idx) => {
        const deviceInfo = deviceTypeMap.server;
        nodes.push({
          id: node.id,
          type: 'custom',
          data: {
            label: node.label || `Server ${node.id}`,
            iconSvg: deviceInfo.icon,
            color: deviceInfo.color,
            ip: node.id,
            hasIncident: incidentIPs.includes(node.id)
          },
          position: { x: serverX, y: layerYStart + (idx * serverSpacingY) }
        });
      });
    }

    // Converti edge dall'API al formato React Flow
    if (topologyData.edges && topologyData.edges.length > 0) {
      edges = topologyData.edges.map((edge) => ({
        id: `edge-${edge.source}-${edge.target}`,
        source: edge.source,
        target: edge.target,
        animated: true,
        style: { stroke: '#cbd5e1', strokeWidth: 2, zIndex: 10 },
        type: 'smoothstep'
      }));
    }
    
  } else if (showMock) {
    // Usa dati MOCK statici SOLO se topologyData === 'mock'
    const pcCount = 7;
    const pcSpacing = 120;
    const startX = 200;

    // IP mapping for devices
    const deviceIPs = {
      router: '10.0.0.1',
      switch: '10.0.0.2',
      ap: '10.0.0.3',
      printer1: '10.0.5.10',
      printer2: '10.0.5.11',
      phone1: '10.0.6.20',
      phone2: '10.0.6.21',
      phone3: '10.0.6.22',
      pc1: '10.0.2.15',
      pc2: '10.0.1.45',
      pc3: '10.0.3.22',
      pc4: '10.0.2.33',
      pc5: '10.0.2.44',
      pc6: '10.0.2.55',
      pc7: '10.0.2.66'
    };

    nodes = [
      { id: 'internet', type: 'custom', data: { label: 'Internet', iconSvg: icons.internet, color: '#60a5fa' }, position: { x: 500, y: 20 } },
      { id: 'router', type: 'custom', data: { label: 'Router', iconSvg: icons.router, color: '#fb923c', ip: deviceIPs.router, hasIncident: incidentIPs.includes(deviceIPs.router) }, position: { x: 500, y: 120 } },
      { id: 'switch', type: 'custom', data: { label: 'Switch', iconSvg: icons.switch, color: '#10b981', ip: deviceIPs.switch, hasIncident: incidentIPs.includes(deviceIPs.switch) }, position: { x: 400, y: 240 } },
      { id: 'ap', type: 'custom', data: { label: 'Access Point', iconSvg: icons.ap, color: '#7c3aed', ip: deviceIPs.ap, hasIncident: incidentIPs.includes(deviceIPs.ap) }, position: { x: 700, y: 240 } },
      { id: 'printer1', type: 'custom', data: { label: t('networkingPage.printer1'), iconSvg: icons.printer, color: '#8b5cf6', ip: deviceIPs.printer1, hasIncident: incidentIPs.includes(deviceIPs.printer1) }, position: { x: 250, y: 240 } },
      { id: 'printer2', type: 'custom', data: { label: t('networkingPage.printer2'), iconSvg: icons.printer, color: '#8b5cf6', ip: deviceIPs.printer2, hasIncident: incidentIPs.includes(deviceIPs.printer2) }, position: { x: 900, y: 120 } },
      { id: 'phone1', type: 'custom', data: { label: t('networkingPage.phone1'), iconSvg: icons.phone, color: '#ec4899', ip: deviceIPs.phone1, hasIncident: incidentIPs.includes(deviceIPs.phone1) }, position: { x: 1020, y: 240 } },
      { id: 'phone2', type: 'custom', data: { label: t('networkingPage.phone2'), iconSvg: icons.phone, color: '#ec4899', ip: deviceIPs.phone2, hasIncident: incidentIPs.includes(deviceIPs.phone2) }, position: { x: 1110, y: 240 } },
      { id: 'phone3', type: 'custom', data: { label: t('networkingPage.phone3'), iconSvg: icons.phone, color: '#ec4899', ip: deviceIPs.phone3, hasIncident: incidentIPs.includes(deviceIPs.phone3) }, position: { x: 1200, y: 240 } },
    ];

    for (let i = 0; i < pcCount; i++) {
      const pcId = `pc${i + 1}`;
      const pcIP = deviceIPs[pcId];
      nodes.push({ id: pcId, type: 'custom', data: { label: `PC ${i + 1}`, iconSvg: icons.pc, color: '#60a5fa', ip: pcIP, hasIncident: incidentIPs.includes(pcIP) }, position: { x: startX + i * pcSpacing, y: 380 } });
    }

    edges = [
      { id: 'e-in-router', source: 'internet', target: 'router', animated: true, style: { stroke: '#cbd5e1', strokeWidth: 3, zIndex: 10 }, label: 'fibra', labelStyle: { fontSize: '12px', fill: '#64748b', fontWeight: 'bold' }, type: 'smoothstep' },
      { id: 'e-router-switch', source: 'router', target: 'switch', animated: true, style: { stroke: '#cbd5e1', strokeWidth: 3, zIndex: 10 }, label: 'LAN', labelStyle: { fontSize: '12px', fill: '#64748b', fontWeight: 'bold' }, type: 'smoothstep' },
      { id: 'e-switch-ap', source: 'switch', target: 'ap', animated: true, style: { stroke: '#cbd5e1', strokeWidth: 3, zIndex: 10 }, type: 'smoothstep' },
      { id: 'e-switch-printer1', source: 'switch', target: 'printer1', animated: true, style: { stroke: '#cbd5e1', strokeWidth: 2, zIndex: 10 }, type: 'smoothstep' },
      { id: 'e-ap-printer2', source: 'ap', target: 'printer2', animated: true, style: { stroke: '#cbd5e1', strokeWidth: 2, zIndex: 10 }, type: 'smoothstep' },
      { id: 'e-ap-phone1', source: 'ap', target: 'phone1', animated: true, style: { stroke: '#cbd5e1', strokeWidth: 2, zIndex: 10 }, type: 'smoothstep' },
      { id: 'e-ap-phone2', source: 'ap', target: 'phone2', animated: true, style: { stroke: '#cbd5e1', strokeWidth: 2, zIndex: 10 }, type: 'smoothstep' },
      { id: 'e-ap-phone3', source: 'ap', target: 'phone3', animated: true, style: { stroke: '#cbd5e1', strokeWidth: 2, zIndex: 10 }, type: 'smoothstep' },
    ];

    for (let i = 0; i < pcCount; i++) {
      edges.push({ id: `e-switch-pc${i + 1}`, source: 'switch', target: `pc${i + 1}`, animated: true, style: { stroke: '#cbd5e1', strokeWidth: 2, zIndex: 10 }, type: 'smoothstep' });
    }
  }  const legendItems = [
    { iconSvg: icons.internet, label: 'Internet', desc: t('networkingPage.legendInternetDesc'), color: '#60a5fa' },
    { iconSvg: icons.router, label: 'Router', desc: t('networkingPage.legendRouterDesc'), color: '#fb923c' },
    { iconSvg: icons.switch, label: 'Switch', desc: t('networkingPage.legendSwitchDesc'), color: '#10b981' },
    { iconSvg: icons.ap, label: 'Access Point', desc: t('networkingPage.legendAPDesc'), color: '#7c3aed' },
    { iconSvg: icons.pc, label: 'PC', desc: t('networkingPage.legendPCDesc'), color: '#60a5fa' },
    { iconSvg: icons.printer, label: t('networkingPage.legendPrinter'), desc: t('networkingPage.legendPrinterDesc'), color: '#8b5cf6' },
    { iconSvg: icons.phone, label: t('networkingPage.legendPhone'), desc: t('networkingPage.legendPhoneDesc'), color: '#ec4899' },
  ];

  const defaultEdgeOptions = {
    animated: false,
    style: { stroke: '#ff0000', strokeWidth: 3 },
  };

  return (
    <div className={className}>
      <div style={{ width: '100%', height: 460, background: '#f9fafb' }}>
        <ReactFlow 
          nodes={nodes} 
          edges={edges} 
          nodeTypes={nodeTypes} 
          defaultEdgeOptions={defaultEdgeOptions}
          fitView 
          attributionPosition="bottom-left" 
          nodesConnectable={false} 
          nodesDraggable={true} 
          panOnScroll={true} 
          zoomOnScroll={true}
          elementsSelectable={true}
          minZoom={0.5}
          maxZoom={2}
        >
          <Background gap={16} size={1} />
          <Controls />
          <MiniMap nodeStrokeColor={(n) => (n.data?.label === 'Router' ? '#92400e' : n.data?.label === 'Switch' ? '#065f46' : '#1e3a8a')} />
        </ReactFlow>
      </div>
      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="font-semibold mb-3 text-gray-800">{t('networkingPage.deviceLegend')}</h4>
        <div className="flex flex-wrap gap-8">
          {legendItems.map((item, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center" style={{ color: item.color }}>
                <div dangerouslySetInnerHTML={{ __html: item.iconSvg.replace('width="70" height="50"', 'width="28" height="21"') }} />
              </div>
              <div className="text-sm font-medium text-gray-900">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// ADMIN PANEL: Classification Patterns Management
// ============================================
function AdminClassificationPatterns({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPattern, setNewPattern] = useState({
    pattern_type: 'asn',
    pattern_value: '',
    classification: 'trusted_partner',
    confidence: 'medium',
    description: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  const patternTypes = [
    { value: 'asn', label: 'ASN Name' },
    { value: 'org', label: 'Organization' },
    { value: 'isp', label: 'ISP' },
    { value: 'ip_prefix', label: 'IP Prefix' },
    { value: 'hostname', label: 'Hostname' }
  ];

  const classifications = [
    { value: 'cdn', label: 'CDN', icon: <Rocket size={14} className="inline" /> },
    { value: 'dns_resolver', label: 'DNS Resolver', icon: <Search size={14} className="inline" /> },
    { value: 'cloud_provider', label: 'Cloud Provider', icon: <Cloud size={14} className="inline" /> },
    { value: 'tier1_carrier', label: 'Tier-1 Carrier', icon: <Globe size={14} className="inline" /> },
    { value: 'isp', label: 'ISP Gateway', icon: <Signal size={14} className="inline" /> },
    { value: 'hosting', label: 'Hosting', icon: <Monitor size={14} className="inline" /> },
    { value: 'security_vendor', label: 'Security Vendor', icon: <Shield size={14} className="inline" /> },
    { value: 'social_media', label: 'Social Media', icon: <Users size={14} className="inline" /> },
    { value: 'streaming', label: 'Streaming', icon: <Tv size={14} className="inline" /> },
    { value: 'gaming', label: 'Gaming', icon: <Gamepad2 size={14} className="inline" /> },
    { value: 'saas', label: 'SaaS', icon: <Briefcase size={14} className="inline" /> },
    { value: 'trusted_partner', label: 'Trusted Partner', icon: <CheckCircle2 size={14} className="inline text-emerald-600" /> },
    { value: 'unknown', label: 'Unknown', icon: <HelpCircle size={14} className="inline" /> }
  ];

  // Fetch patterns on open
  useEffect(() => {
    if (isOpen) {
      fetchPatterns();
      fetchStats();
    }
  }, [isOpen]);

  const fetchPatterns = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.netflowClassificationPatterns);
      if (res.ok) {
        const data = await res.json();
        setPatterns(data.patterns || []);
      }
    } catch (err) {
      console.error('[Admin] Failed to fetch patterns:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.netflowClassificationPatternsStats);
      if (res.ok) {
        const data = await res.json();
        setStats(data.cache || data);
      }
    } catch (err) {
      console.error('[Admin] Failed to fetch stats:', err);
    }
  };

  const handleAddPattern = async () => {
    if (!newPattern.pattern_value.trim()) {
      alert(t('networkingPage.alertPatternValue'));
      return;
    }
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.netflowClassificationPatternsAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPattern)
      });
      if (res.ok) {
        await fetchPatterns();
        await fetchStats();
        setShowAddForm(false);
        setNewPattern({ pattern_type: 'asn', pattern_value: '', classification: 'trusted_partner', confidence: 'medium', description: '' });
      } else {
        alert(t('networkingPage.alertPatternAddError'));
      }
    } catch (err) {
      alert(t('networkingPage.alertNetworkError'));
    }
  };

  const handleDeletePattern = async (patternId) => {
    if (!window.confirm(t('networkingPage.alertDeleteConfirm'))) return;
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.netflowClassificationPatternsDelete(patternId), {
        method: 'DELETE'
      });
      if (res.ok) {
        await fetchPatterns();
        await fetchStats();
      } else {
        alert(t('networkingPage.alertDeleteError'));
      }
    } catch (err) {
      alert(t('networkingPage.alertNetworkError'));
    }
  };

  const handleReloadCache = async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.netflowClassificationPatternsReload, {
        method: 'POST'
      });
      if (res.ok) {
        await fetchStats();
        alert(t('networkingPage.alertCacheReloaded'));
      }
    } catch (err) {
      alert(t('networkingPage.alertCacheError'));
    }
  };

  // Filter patterns
  const filteredPatterns = patterns.filter(p => {
    const matchesSearch = !searchQuery || 
      p.pattern_value.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || p.pattern_type === filterType;
    return matchesSearch && matchesType;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] md:w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Settings size={20} className="inline" /> {t('networkingPage.adminTitle')}
            </h2>
            <p className="text-blue-100 text-sm mt-1">{t('networkingPage.adminSubtitle')}</p>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 p-2 rounded-full transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="bg-gray-50 border-b px-6 py-3 flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">{t('networkingPage.totalPatterns')}</span>
              <span className="font-bold text-blue-600">{stats.total_patterns || 0}</span>
            </div>
            <div className="flex items-center gap-4">
              {Object.entries(stats.pattern_counts || {}).map(([type, count]) => (
                <span key={type} className="px-2 py-0.5 bg-gray-200 rounded text-xs">
                  {type}: {count}
                </span>
              ))}
            </div>
            <button 
              onClick={handleReloadCache}
              className="ml-auto text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1"
            >
              <><RefreshCw size={12} className="inline" /> {t('networkingPage.reloadCache')}</>
            </button>
          </div>
        )}

        {/* Toolbar */}
        <div className="p-4 border-b flex items-center gap-4">
          <input
            type="text"
            placeholder={t('networkingPage.searchPatterns')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-3 py-2 border rounded-lg text-sm"
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="all">{t('networkingPage.allTypes')}</option>
            {patternTypes.map(pt => (
              <option key={pt.value} value={pt.value}>{pt.label}</option>
            ))}
          </select>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2"
          >
            <Plus size={14} className="inline" /> {t('networkingPage.addPattern')}
          </button>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <div className="p-4 bg-green-50 border-b border-green-200">
            <h3 className="font-semibold mb-3 text-green-800">{t('networkingPage.newPatternTitle')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">{t('networkingPage.patternType')}</label>
                <select
                  value={newPattern.pattern_type}
                  onChange={(e) => setNewPattern({...newPattern, pattern_type: e.target.value})}
                  className="w-full px-2 py-1.5 border rounded text-sm"
                >
                  {patternTypes.map(pt => (
                    <option key={pt.value} value={pt.value}>{pt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">{t('networkingPage.valueLabel')}</label>
                <input
                  type="text"
                  value={newPattern.pattern_value}
                  onChange={(e) => setNewPattern({...newPattern, pattern_value: e.target.value})}
                  placeholder="es. cloudflare, 8.8.8."
                  className="w-full px-2 py-1.5 border rounded text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">{t('networkingPage.classificationLabel')}</label>
                <select
                  value={newPattern.classification}
                  onChange={(e) => setNewPattern({...newPattern, classification: e.target.value})}
                  className="w-full px-2 py-1.5 border rounded text-sm"
                >
                  {classifications.map(c => (
                    <option key={c.value} value={c.value}>{c.icon} {c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">{t('networkingPage.confidenceLabel')}</label>
                <select
                  value={newPattern.confidence}
                  onChange={(e) => setNewPattern({...newPattern, confidence: e.target.value})}
                  className="w-full px-2 py-1.5 border rounded text-sm"
                >
                  <option value="high">{t('networkingPage.confidenceHigh')}</option>
                  <option value="medium">{t('networkingPage.confidenceMedium')}</option>
                  <option value="low">{t('networkingPage.confidenceLow')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">{t('networkingPage.descriptionLabel')}</label>
                <input
                  type="text"
                  value={newPattern.description}
                  onChange={(e) => setNewPattern({...newPattern, description: e.target.value})}
                  placeholder={t('networkingPage.optional')}
                  className="w-full px-2 py-1.5 border rounded text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={handleAddPattern} className="px-4 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700">{t('networkingPage.save')}</button>
              <button onClick={() => setShowAddForm(false)} className="px-4 py-1.5 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400">{t('networkingPage.cancel')}</button>
            </div>
          </div>
        )}

        {/* Patterns Table */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">{t('networkingPage.loadingPatterns')}</div>
          ) : filteredPatterns.length === 0 ? (
            <div className="text-center py-8 text-gray-500">{t('networkingPage.noPatternsFound')}</div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left">{t('networkingPage.typeHeader')}</th>
                  <th className="px-3 py-2 text-left">{t('networkingPage.valueHeader')}</th>
                  <th className="px-3 py-2 text-left">{t('networkingPage.classificationHeader')}</th>
                  <th className="px-3 py-2 text-left">{t('networkingPage.confidenceHeader')}</th>
                  <th className="px-3 py-2 text-left">{t('networkingPage.descriptionHeader')}</th>
                  <th className="px-3 py-2 text-left">{t('networkingPage.statusHeader')}</th>
                  <th className="px-3 py-2 text-center">{t('networkingPage.actionsHeader')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredPatterns.map((p) => {
                  const classInfo = classifications.find(c => c.value === p.classification) || { icon: <HelpCircle size={14} className="inline" />, label: p.classification };
                  return (
                    <tr key={p.id} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">{p.pattern_type}</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-sm">{p.pattern_value}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1">
                          <span>{classInfo.icon}</span>
                          <span>{classInfo.label}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          p.confidence === 'high' ? 'bg-green-100 text-green-800' :
                          p.confidence === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {p.confidence}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600 text-xs">{p.description || '—'}</td>
                      <td className="px-3 py-2">
                        {p.is_active ? (
                          <span className="text-green-600"><Check size={14} className="inline" /> {t('networkingPage.active')}</span>
                        ) : (
                          <span className="text-gray-400">{t('networkingPage.inactive')}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => handleDeletePattern(p.id)}
                          className="text-red-600 hover:text-red-800 text-xs"
                        >
                          <><Trash2 size={12} className="inline" /> {t('networkingPage.deleteBtn')}</>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// TOPOLOGY HISTORY - Timeline snapshots
// ============================================
function TopologyHistoryPanel({ tenantId, isOpen, onClose, onSelectSnapshot }) {
  const { t } = useTranslation();
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (isOpen && tenantId) {
      fetchHistory();
    }
  }, [isOpen, tenantId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.netflowTopologyHistory(tenantId) + '?limit=20');
      if (res && res.ok) {
        const data = await res.json();
        // Supporta diversi formati di risposta: {snapshots: [...]}, {results: [...]}, [...]
        let snapshotsArray = [];
        if (Array.isArray(data)) {
          snapshotsArray = data;
        } else if (data.snapshots && Array.isArray(data.snapshots)) {
          snapshotsArray = data.snapshots;
        } else if (data.results && Array.isArray(data.results)) {
          snapshotsArray = data.results;
        } else if (data.history && Array.isArray(data.history)) {
          snapshotsArray = data.history;
        }
        setSnapshots(snapshotsArray);
      } else {
        console.warn('[TopologyHistory] Fetch failed with status:', res?.status);
      }
    } catch (err) {
      console.error('[TopologyHistory] Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (snapshot) => {
    setSelectedId(snapshot.id);
    if (onSelectSnapshot) {
      onSelectSnapshot(snapshot);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 h-full w-80 bg-white shadow-2xl z-40 flex flex-col">
      <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white p-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold flex items-center gap-2">
            <ScrollText size={18} className="inline" /> {t('networkingPage.topologyHistory')}
          </h3>
          <p className="text-yellow-100 text-xs">{t('networkingPage.browseSnapshots')}</p>
        </div>
        <button onClick={onClose} className="text-white hover:bg-white/20 p-1 rounded">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="text-center py-8 text-gray-500">{t('networkingPage.loadingHistory')}</div>
        ) : snapshots.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2"><MailX size={40} /></div>
            <p>{t('networkingPage.noSnapshots')}</p>
            <p className="text-xs mt-2">{t('networkingPage.snapshotsAutoCreated')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {snapshots.map((snap, idx) => (
              <button
                key={snap.id || idx}
                onClick={() => handleSelect(snap)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  selectedId === snap.id 
                    ? 'border-yellow-500 bg-yellow-50 shadow' 
                    : 'border-gray-200 hover:border-yellow-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-500">
                    {new Date(snap.timestamp || snap.created_at).toLocaleDateString('it-IT', {
                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </span>
                  {selectedId === snap.id && (
                    <span className="text-yellow-600 text-xs font-medium"><Check size={12} className="inline" /> {t('networkingPage.selected')}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <span className="flex items-center gap-1">
                    <span className="text-blue-500">●</span>
                    <span>{snap.node_count || snap.nodes?.length || '?'} {t('networkingPage.nodesLabel')}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-green-500">—</span>
                    <span>{snap.edge_count || snap.edges?.length || '?'} {t('networkingPage.connectionsLabel')}</span>
                  </span>
                </div>
                {snap.incident_count > 0 && (
                  <div className="mt-1 text-xs text-red-600">
                    <><AlertTriangle size={12} className="inline" /> {snap.incident_count} {t('networkingPage.activeIncidents')}</>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 border-t bg-gray-50">
        <button 
          onClick={fetchHistory}
          className="w-full py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600"
        >
          <><RefreshCw size={14} className="inline" /> {t('networkingPage.refreshHistory')}</>
        </button>
      </div>
    </div>
  );
}

export default function Networking() {
  // Example dynamic-like data (placeholders)
  const [range, setRange] = useState('60m');
  const [topologyOpen, setTopologyOpen] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [topologyData, setTopologyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantData, setTenantData] = useState(null);
  const [hasNetworkingBundle, setHasNetworkingBundle] = useState(false);
  const [checkedBundle, setCheckedBundle] = useState(false); // per evitare flicker
  const [error, setError] = useState(null);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const location = useLocation();
  const [incidentFilters, setIncidentFilters] = useState({ priority: 'all', status: 'open_in_progress' });
  const [incidentPage, setIncidentPage] = useState(1);
  const INCIDENTS_PER_PAGE = 5;
  const [reloadCounter, setReloadCounter] = useState(0);
  const [providerRangeUnsupported, setProviderRangeUnsupported] = useState(false);
  const prevRangeRef = React.useRef(null);
  const prevFlowsRef = React.useRef(null);
  
  // State per pannelli History (Priority Low features)
  const [showTopologyHistory, setShowTopologyHistory] = useState(false);
  
  const { t } = useTranslation();
  // Range labels mapping
  const rangeLabels = {
    '1h': t('networkingPage.lastHour'),
    '24h': t('networkingPage.last24h'),
    '7d': t('networkingPage.last7d')
  };
  
  // Handlers per il modal incidenti
  // Usa sempre remote_id per le API
  const handleUpdateStatus = async (incidentId, newStatus) => {
    if (!tenantData || !tenantData.id) return;
    // Trova l'incidente e prendi solo id numerico
    const incident = incidents.find(inc => inc.id === incidentId || String(inc.remote_id) === String(incidentId) || inc.remote_id === incidentId);
    // Preferisci sempre il remote numeric id se disponibile
    const numericId = incident?.remote_id ? Number(incident.remote_id) : Number(incidentId);
    
    // Lo status inviato a NetFlow è in formato italiano maiuscolo: CONFERMATO, FALSO POSITIVO, CHIUSO, IN CORSO
    // Per lo stato locale normalizziamo a lowercase per coerenza con il display
    const normalizeLocalStatus = (s) => {
      const upper = (s || '').toUpperCase();
      if (upper === 'FALSO POSITIVO' || upper === 'FALSO_POSITIVO' || upper === 'FALSE_POSITIVE') return 'falso positivo';
      if (upper === 'CONFERMATO' || upper === 'CONFIRMED') return 'confermato';
      if (upper === 'CHIUSO' || upper === 'CLOSED') return 'chiuso';
      if (upper === 'IN CORSO' || upper === 'IN_CORSO' || upper === 'IN-PROGRESS') return 'in-progress';
      if (upper === 'APERTO' || upper === 'OPEN') return 'open';
      return s.toLowerCase();
    };
    
    try {
      const urlNum = API_ENDPOINTS.updateNetflowIncidentStatus(tenantData.id, numericId);
      const resNum = await fetchWithAuth(urlNum, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (resNum.ok) {
        const result = await resNum.json();
        const localStatus = normalizeLocalStatus(newStatus);
        setIncidents(prev => prev.map(inc => 
          // aggiorna l'incidente corrispondente sia se confronto per formatted id (inc.id) sia per remote_id
          (inc.id === incidentId || String(inc.remote_id) === String(numericId)) ? { ...inc, status: localStatus, closed_at: (['confermato', 'falso positivo', 'chiuso'].includes(localStatus)) ? new Date().toISOString() : null } : inc
        ));
        setSelectedIncident(null);
      } else {
        // Leggi eventuale body di risposta per diagnosticare
        let errBody = null;
        try { errBody = await resNum.json(); } catch (e) { errBody = await resNum.text().catch(() => null); }
        console.error('[NetFlow][DEBUG] Errore nel salvataggio stato:', resNum.status, urlNum, errBody);
        alert(t('networkingPage.alertSaveStatusError') + ` (status ${resNum.status})` + (errBody ? `: ${typeof errBody === 'string' ? errBody : JSON.stringify(errBody)}` : ''));
      }
    } catch (err) {
      // Non aggiornare lo stato localmente se la chiamata fallisce
      alert(t('networkingPage.alertNetworkStatusError'));
      console.error('[NetFlow][DEBUG] Errore di rete (numericId):', err);
    }
  };

  const handleAddComment = async (incidentId, commentText) => {
    if (!tenantData || !tenantData.id) return;
    // Trova l'incidente e prendi il remote_id (resiliente a tipi e formati)
    const incident = incidents.find(inc => inc.id === incidentId || String(inc.remote_id) === String(incidentId) || inc.remote_id === incidentId);
    const remoteId = incident?.remote_id ? Number(incident.remote_id) : (Number(incidentId) || incidentId);
    try {
      const url = API_ENDPOINTS.addNetflowIncidentComment(tenantData.id, remoteId);
      const res = await fetchWithAuth(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: commentText })
      });
      if (res.ok) {
        const result = await res.json();
        const newComment = { user: result.user || 'current_user', timestamp: new Date().toLocaleTimeString(), text: commentText };
        setIncidents(prev => prev.map(inc => 
          inc.id === incidentId ? { ...inc, comments: [...(inc.comments || []), newComment] } : inc
        ));
      } else {
        alert(t('networkingPage.alertSaveCommentError'));
      }
    } catch (err) {
      alert(t('networkingPage.alertNetworkCommentError'));
    }
  };

  // Forza fetch diretto della topologia (invocato dal bottone "Aggiorna topologia")
  const handleForceTopologyUpdate = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setLoading(true);
    try {
      // preferisci tenantData se disponibile
      const tenantId = tenantData?.id;
      if (!tenantId) {
        console.warn('[NetFlow] No tenantData available to fetch topology');
        setLoading(false);
        return;
      }
      const topologyUrl = `${API_ENDPOINTS.netflowTenants}${tenantId}/topology/?minutes=60`;
      const topologyRes = await fetchWithAuth(topologyUrl);
      if (topologyRes && topologyRes.ok) {
        const topoData = await topologyRes.json();
        const hasNodes = topoData && topoData.nodes && topoData.nodes.length > 0;
        setTopologyData(hasNodes ? topoData : 'mock');

        // If we have topology but no dashboardData (or no flows), seed a minimal
        // dashboardData so the UI will render the TopologyDiagram (it checks
        // `dashboardData && dashboardData.total_flows > 0` before rendering).
        if (hasNodes && (!dashboardData || !dashboardData.total_flows || dashboardData.total_flows === 0)) {
          const inferredDashboard = {
            total_flows: (topoData.stats && topoData.stats.total_nodes) || topoData.nodes.length || 1,
            total_anomalies: (topoData.stats && topoData.stats.anomalies) || 0,
            status: 'HEALTHY',
            timestamp: (topoData.stats && topoData.stats.timestamp) || new Date().toISOString(),
          };
          setDashboardData(inferredDashboard);
        }
      } else {
        console.warn('[NetFlow] Force Topology proxy fetch failed:', topologyRes?.status);
      }
    } catch (err) {
      console.error('[NetFlow] Error force fetching topology:', err);
    } finally {
      setLoading(false);
      // bump reload counter as well to trigger broader refresh flows
      setReloadCounter(c => c + 1);
    }
  };

  // If the page is opened with deep-link params (from a notification), open the specific incident
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const incidentParam = params.get('incident');
      const tenantParam = params.get('tenant');
      if (!incidentParam) return;
      // ensure tenant matches current tenant loaded
      if (!tenantData || !tenantData.id) return;
      if (String(tenantData.id) !== String(tenantParam)) return;

      // find the incident (by remote id or local id)
      const found = incidents.find(inc => String(inc.remote_id) === String(incidentParam) || String(inc.id) === String(incidentParam));
      if (found) {
        try {
          const handledKey = `handled_incident_${tenantParam || '0'}_${incidentParam}`;
          const alreadyHandled = sessionStorage.getItem(handledKey);
          if (!alreadyHandled) {
            setSelectedIncident(found);
            // mark as handled in sessionStorage to avoid re-opening on subsequent reloads/actions
            try { sessionStorage.setItem(handledKey, Date.now().toString()); } catch (e) { /* ignore */ }
            // Mark notifications related to this incident as read on the server
            (async () => {
              try {
                await fetchWithAuth(API_ENDPOINTS.notificationsMarkByIncident, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ incident: incidentParam })
                });
              } catch (e) {
                console.warn('[Notifications] mark by incident failed', e);
              }
            })();
          }
          // always try to remove the incident/tenant params from the URL
          try {
            const newParams = new URLSearchParams(location.search);
            newParams.delete('incident');
            newParams.delete('tenant');
            const newSearch = newParams.toString();
            const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
            window.history.replaceState({}, document.title, newUrl);
          } catch (e) { /* ignore history errors */ }
        } catch (e) {
          // ignore
        }
      }
    } catch (e) {
      // ignore parsing errors
    }
  }, [incidents, tenantData, location.search]);
  
  const totals = useMemo(() => ({
    flows: dashboardData?.total_flows !== undefined ? dashboardData.total_flows.toLocaleString() : '0',
    anomalies: dashboardData?.total_anomalies !== undefined ? dashboardData.total_anomalies : 0,
    anomalyRate: dashboardData?.anomaly_rate || '0.0%',
    sessions: dashboardData?.total_sessions || 0
  }), [dashboardData]);
  
  const sparkValues = dashboardData?.flow_history || [];
  // ML score trend: prefer dashboard-provided series, fallback to recent incidents' ml_score
  // NOTA: ml_score è già 0-1, non moltiplicarlo per 100 due volte
  const mlTrend = dashboardData?.ml_score_history || (incidents && incidents.length > 0 ? incidents.slice(-8).map(i => Number(i.ml_score || 0)) : [0.7, 0.72, 0.74, 0.75, 0.76]);
  const mlCurrent = (mlTrend && mlTrend.length > 0) ? Number(mlTrend[mlTrend.length - 1]) : null;
  // Assicurati che mlCurrent sia sempre 0-1 prima di moltiplicare per 100
  const mlDisplay = mlCurrent !== null ? `${Math.min(100, (mlCurrent * 100)).toFixed(1)}%` : '—';
  
  // Calcola le percentuali della distribuzione protocolli
  const protocolRaw = {
    TCP: dashboardData?.protocol_distribution?.TCP || 0,
    UDP: dashboardData?.protocol_distribution?.UDP || 0,
    ICMP: dashboardData?.protocol_distribution?.ICMP || 0
  };
  const protocolTotal = protocolRaw.TCP + protocolRaw.UDP + protocolRaw.ICMP;
  const protocol = [
    { name: 'TCP', value: protocolTotal > 0 ? ((protocolRaw.TCP / protocolTotal) * 100).toFixed(1) : 0 },
    { name: 'UDP', value: protocolTotal > 0 ? ((protocolRaw.UDP / protocolTotal) * 100).toFixed(1) : 0 },
    { name: 'ICMP', value: protocolTotal > 0 ? ((protocolRaw.ICMP / protocolTotal) * 100).toFixed(1) : 0 }
  ];

  // Load dati reali dalla API NetFlow
  useEffect(() => {
    let mounted = true;
    
    async function loadNetflowData() {
      // Azzera la topologia prima di ogni nuova fetch per evitare flicker vecchi dati
      setTopologyData(null);
      try {
        setLoading(true);
        setError(null);
        
        // 1. Recupera il primo tenant provisionato (che contiene info se ha il bundle)
        // Se l'URL contiene ?azienda_id=... usalo per richiedere il tenant per quell'azienda
        const urlParams = new URLSearchParams(window.location.search);
        let aziendaIdParam = urlParams.get('azienda_id');

        // Se non è passato in URL, prova a recuperare l'azienda corrente dall'API
        if (!aziendaIdParam) {
          try {
            const aziendaRes = await fetchWithAuth(API_ENDPOINTS.aziendaDettagli);
            if (aziendaRes && aziendaRes.ok) {
              const aziendaJson = await aziendaRes.json();
              aziendaIdParam = aziendaJson?.id_azienda || aziendaJson?.id || aziendaJson?.external_id;
            } else {
              console.warn('[NetFlow] Azienda details fetch failed:', aziendaRes?.status);
            }
          } catch (e) {
            console.warn('[NetFlow] Errore nel recupero azienda corrente:', e);
          }
        }

        const tenantsUrl = aziendaIdParam ? `${API_ENDPOINTS.netflowTenants}?azienda_id=${aziendaIdParam}` : API_ENDPOINTS.netflowTenants;
        const tenantRes = await fetchWithAuth(tenantsUrl);
        if (!tenantRes || !tenantRes.ok) {
          console.error('[NetFlow] Tenant fetch failed:', tenantRes?.status);
          if (mounted) setError(t('networkingPage.unauthorizedNetworking'));
          if (mounted) setLoading(false);
          return;
        }
        
        const tenantJson = await tenantRes.json();
        
        // tenantJson può essere paginato ({results: [...]}) oppure un array.
        const tenantsArray = Array.isArray(tenantJson) ? tenantJson : (tenantJson?.results || []);

        // Se ci sono più tenant, preferisci prima tenant che hanno l'offerta impostata
        // (es. 'PREMIUM','SMART' ecc.) — questo evita che tenant con campo
        // offerta 'false' blocchino la selezione quando esistono tenant realmente attivi.
        let firstTenant = null;
        if (tenantsArray.length > 0) {
          // Preferenza veloce: tenant con offerta dichiarata != 'false'
          const preferredLocal = tenantsArray.find(t => {
            const v = t?.offerta_sorveglianza_rete;
            return v && String(v).trim().toLowerCase() !== 'false' && String(v).trim() !== '';
          });
          if (preferredLocal) {
            firstTenant = preferredLocal;
            if (mounted) setHasNetworkingBundle(true);
          } else {
            // Prova a trovare tenant con remote_tenant_id e bundle attivo interrogando il server
            for (const t of tenantsArray) {
              try {
                const bundleUrl = `${API_ENDPOINTS.netflowTenants}${t.id}/bundle-status/`;
                const bundleRes = await fetchWithAuth(bundleUrl);
                if (bundleRes && bundleRes.ok) {
                  const bundleData = await bundleRes.json();
                  if (bundleData?.has_networking_bundle && bundleData?.provisioned) {
                    firstTenant = t;
                    if (mounted) setHasNetworkingBundle(true);
                    break;
                  }
                }
              } catch (e) {
                console.warn('[NetFlow] Errore bundle-status per tenant', t.id, e);
              }
            }

            // Se non abbiamo trovato tenant con bundle attivo, preferisci comunque un tenant provisioned
            if (!firstTenant) {
              const provisioned = tenantsArray.find(x => x.remote_tenant_id);
              firstTenant = provisioned || tenantsArray[0] || null;
              if (mounted) setHasNetworkingBundle(!!(firstTenant?.offerta_sorveglianza_rete));
            }
          }
        }
        
        if (!mounted) return;
        setTenantData(firstTenant);
        
        // Verifica lato server se l'azienda ha il bundle networking (source of truth)
        let serverHasBundle = false;
        try {
          const bundleUrl = `${API_ENDPOINTS.netflowTenants}${firstTenant.id}/bundle-status/`;
          const bundleRes = await fetchWithAuth(bundleUrl);
          if (bundleRes && bundleRes.ok) {
            const bundleData = await bundleRes.json();
            serverHasBundle = !!bundleData.has_networking_bundle;
            // if provisioned false but bundle true, we still show informative message later
            if (mounted) setHasNetworkingBundle(serverHasBundle);
          } else {
            console.warn('[NetFlow] bundle-status fetch failed:', bundleRes?.status);
            // Fallback: usa il campo locale se presente
            serverHasBundle = firstTenant?.offerta_sorveglianza_rete && firstTenant.offerta_sorveglianza_rete !== 'false' && firstTenant.offerta_sorveglianza_rete !== '';
            if (mounted) setHasNetworkingBundle(serverHasBundle);
          }
        } catch (e) {
          console.warn('[NetFlow] Errore nel recupero bundle-status:', e);
          serverHasBundle = firstTenant?.offerta_sorveglianza_rete && firstTenant.offerta_sorveglianza_rete !== 'false' && firstTenant.offerta_sorveglianza_rete !== '';
          if (mounted) setHasNetworkingBundle(serverHasBundle);
        }

        // Se il server dice che non c'è il bundle, non mostrare nulla
        if (!serverHasBundle) {
          console.warn('[NetFlow] Azienda non ha il bundle networking (server)');
          if (mounted) setIncidents([]);
          if (mounted) setLoading(false);
          if (mounted) setCheckedBundle(true);
          return;
        }
        if (mounted) setCheckedBundle(true);
        
        // Se non c'è un tenant provisionato, mostra messaggio
        if (!firstTenant || !firstTenant.id) {
          console.warn('[NetFlow] Nessun tenant trovato per questa azienda');
          if (mounted) setIncidents([]);
          if (mounted) setLoading(false);
          return;
        }
        
        
        // 2. Recupera dashboard dati reali da NetFlow
        try {
          const dashboardUrl = `${API_ENDPOINTS.netflowTenants}${firstTenant.id}/dashboard/?time_range=${range}`;
          const dashboardRes = await fetchWithAuth(dashboardUrl);
          if (dashboardRes && dashboardRes.ok) {
            const dashData = await dashboardRes.json();
              if (mounted) {
                // Detect if provider ignores time_range: if we changed range and the
                // total_flows didn't change, flag it so UI can inform the user.
                const prevRange = prevRangeRef.current;
                const prevFlows = prevFlowsRef.current;
                if (prevRange && prevRange !== range && typeof prevFlows === 'number' && typeof dashData.total_flows === 'number') {
                  if (prevFlows === dashData.total_flows) {
                    setProviderRangeUnsupported(true);
                  } else {
                    setProviderRangeUnsupported(false);
                  }
                }
                prevRangeRef.current = range;
                prevFlowsRef.current = dashData.total_flows;
                setDashboardData(dashData);
              }
          } else {
            console.warn('[NetFlow] Dashboard fetch failed:', dashboardRes?.status);
          }
        } catch (e) {
          console.warn('[NetFlow] Errore nel recupero dashboard:', e);
        }
        
        // 3. Recupera incidenti reali da NetFlow
        try {
          const incidentsUrl = `${API_ENDPOINTS.netflowTenants}${firstTenant.id}/incidents/?time_range=${range}`;
          const incidentsRes = await fetchWithAuth(incidentsUrl);
          if (incidentsRes && incidentsRes.ok) {
            const incData = await incidentsRes.json();
            // DEBUG: stampa la struttura del primo incidente
            let firstIncident = null;
            if (Array.isArray(incData) && incData.length > 0) {
              firstIncident = incData[0];
            } else if (incData?.incidents && incData.incidents.length > 0) {
              firstIncident = incData.incidents[0];
            }
            if (firstIncident) {
            }
            // Verifica se incData è un array o ha una proprietà 'incidents'
            const incidentsArray = Array.isArray(incData) ? incData : (incData?.incidents || []);
            
            if (mounted) {
              if (incidentsArray.length > 0) {
                // Mappa i dati da NetFlow al formato del componente
                const mappedIncidents = incidentsArray.map((inc) => {
                  // Normalizza status NetFlow: APERTO->open, IN CORSO->in-progress, CONFERMATO->confermato, FALSO POSITIVO->falso positivo, CHIUSO->chiuso
                  let normalizedStatus = (inc.status || 'OPEN').toUpperCase();
                  if (normalizedStatus === 'APERTO' || normalizedStatus === 'OPEN') normalizedStatus = 'open';
                  else if (normalizedStatus === 'IN CORSO' || normalizedStatus === 'IN_CORSO') normalizedStatus = 'in-progress';
                  else if (normalizedStatus === 'CONFERMATO' || normalizedStatus === 'CONFIRMED') normalizedStatus = 'confermato';
                  else if (normalizedStatus === 'FALSO POSITIVO' || normalizedStatus === 'FALSO_POSITIVO' || normalizedStatus === 'FALSE_POSITIVE') normalizedStatus = 'falso positivo';
                  else if (normalizedStatus === 'CHIUSO' || normalizedStatus === 'CLOSED') normalizedStatus = 'chiuso';
                  else normalizedStatus = normalizedStatus.toLowerCase();

                  const threat = (inc.threat_type || '').toLowerCase();
                  const desc = (inc.description || '').toString();
                  const srcIp = inc.source_ip || inc.src_addr || '';
                  const ml = parseFloat(inc.ml_score) || 0.5;

                  // Costruisci eventi e azioni consigliate in base al tipo di minaccia
                  const events = [];
                  const recommended_actions = [];

                  if (threat.includes('data_exfiltration') || /upload/i.test(desc)) {
                    // Estrai dimensione e ip di destinazione dalla description
                    const sizeMatch = desc.match(/([0-9]+\.[0-9]+|[0-9]+)\s*MB/i);
                    const size = sizeMatch ? `${sizeMatch[1]} MB` : null;
                    const destMatch = desc.match(/to\s+([0-9]{1,3}(?:\.[0-9]{1,3}){3})/i) || desc.match(/to:\s*([0-9]{1,3}(?:\.[0-9]{1,3}){3})/i);
                    const destIp = destMatch ? destMatch[1] : null;

                    events.push({ type: 'ML Anomaly', timestamp: inc.created_at, description: size ? `Rilevato upload di ${size}` : 'Rilevato upload sospetto' });
                    events.push({ type: 'Correlazione', timestamp: inc.created_at, description: destIp ? `Traffico verso IP esterno non autorizzato: ${destIp}` : 'Traffico verso IP esterno non autorizzato' });
                    if (destIp) {
                      events.push({ type: 'IoC Match', timestamp: inc.created_at, description: 'IP destinazione presente in threat feed' });
                    }

                    if (destIp) recommended_actions.push(`Bloccare l'IP di destinazione ${destIp} sul firewall`);
                    recommended_actions.push(`Verificare quali dati sono stati esfiltrati da ${srcIp || 'host coinvolto'}`);
                    recommended_actions.push("Resettare le credenziali dell'utente coinvolto");
                    recommended_actions.push('Avviare analisi forense completa');
                  } else if (threat.includes('c2') || threat.includes('beacon')) {
                    // Estrai conteggio e peer dalla description
                    const countMatch = desc.match(/(\d+)\s*volte|(\d+) attempts|contattato\s+[0-9.]+:\d+\s*(\d+)?/i);
                    const count = countMatch ? (countMatch[1] || countMatch[2] || countMatch[3]) : null;
                    const peerMatch = desc.match(/(\d{1,3}(?:\.\d{1,3}){3}):\d+/);
                    const peer = peerMatch ? peerMatch[1] : null;

                    events.push({ type: 'ML Anomaly', timestamp: inc.created_at, description: count ? `Rilevati ${count} contatti verso C2` : 'Rilevato pattern di connessione ripetitivo' });
                    if (peer) events.push({ type: 'IoC Match', timestamp: inc.created_at, description: 'IP destinazione presente in threat feed' });
                    if (peer) events.push({ type: 'Peer', timestamp: inc.created_at, description: `Peer: ${peer}` });

                    recommended_actions.push('Isolare immediatamente l\'host compromesso');
                    recommended_actions.push('Bloccare comunicazioni verso i C2 noti e peer rilevati');
                    recommended_actions.push('Eseguire analisi forense sulle connessioni in uscita');
                  } else if (threat.includes('ssh') || threat.includes('bruteforce')) {
                    const attemptsMatch = desc.match(/(\d+)\s*tentativi|ha effettuato\s*(\d+)\s*tentativi/i);
                    const attempts = attemptsMatch ? (attemptsMatch[1] || attemptsMatch[2]) : null;
                    const attackerMatch = desc.match(/(\d{1,3}(?:\.\d{1,3}){3})/);
                    const attacker = attackerMatch ? attackerMatch[1] : srcIp || null;

                    events.push({ type: 'ML Anomaly', timestamp: inc.created_at, description: attempts ? `${attempts} tentativi SSH rilevati` : 'Tentativi SSH multipli rilevati' });
                    if (attacker) events.push({ type: 'IoC Match', timestamp: inc.created_at, description: 'IP sorgente presente in threat feed' });
                    if (attacker) events.push({ type: 'Attaccante', timestamp: inc.created_at, description: `IP sorgente: ${attacker}` });

                    if (attacker) recommended_actions.push(`Bloccare l'IP sorgente ${attacker} sul firewall`);
                    recommended_actions.push('Disabilitare l\'autenticazione password su SSH e usare chiavi');
                    recommended_actions.push('Controllare i log di accesso e cambiare credenziali compromesse');
                  } else {
                    // Default generic event
                    events.push({ type: 'Rilevamento ML', timestamp: inc.created_at, description: desc || 'Anomalia rilevata dal motore ML' });
                    recommended_actions.push('Analizzare i log correlati');
                    recommended_actions.push('Valutare eventuale isolamento dell\'host');
                  }

                  return {
                    id: inc.formatted_id || inc.id,
                    remote_id: inc.id,
                    tenant_id: firstTenant.id, // aggiungi tenant_id per fetch dettagli
                    severity: (inc.severity || 'MEDIUM').toLowerCase(),
                    status: normalizedStatus,
                    threat_type: inc.threat_type || 'unknown',
                    affected_ip: srcIp || null,
                    // Includi i campi IP per il modal
                    source_ip: inc.source_ip || inc.src_addr || null,
                    dest_ip: inc.dest_ip || inc.destination_ip || inc.dst_addr || null,
                    destination_ip: inc.dest_ip || inc.destination_ip || inc.dst_addr || null,
                    // Includi anche i dettagli di classificazione se presenti
                    details: inc.details || null,
                    source_ip_classification: inc.source_ip_classification || inc.details?.source_ip_classification || null,
                    dest_ip_classification: inc.dest_ip_classification || inc.details?.dest_ip_classification || null,
                    // MITRE ATT&CK
                    mitre_technique: inc.mitre_technique || inc.details?.mitre_technique || null,
                    mitre_technique_name: inc.mitre_technique_name || inc.details?.mitre_technique_name || null,
                    mitre_tactic: inc.mitre_tactic || inc.details?.mitre_tactic || null,
                    mitre_description: inc.mitre_description || inc.details?.mitre_description || null,
                    // Repeat offender info
                    is_repeat_offender: inc.is_repeat_offender || inc.details?.is_repeat_offender || false,
                    repeat_offender_count: inc.repeat_offender_count || inc.details?.repeat_offender_count || 0,
                    // Baseline info
                    baseline_deviation: inc.baseline_deviation || inc.details?.baseline_deviation || null,
                    ml_score: ml,
                    source: (inc.source || 'NETFLOW').toLowerCase(),
                    opened_at: inc.created_at || inc.opened_at || new Date().toISOString(),
                    closed_at: null,
                    events,
                    recommended_actions,
                    comments: [],
                    description: inc.description || null
                  };
                });
                setIncidents(mappedIncidents);
              } else {
                setIncidents([]);
              }
            }
          } else {
            console.warn('[NetFlow] Incidents fetch failed:', incidentsRes?.status);
            if (mounted) setIncidents([]);
          }
        } catch (e) {
          console.warn('[NetFlow] Errore nel recupero incidenti:', e);
          if (mounted) setIncidents([]);
        }
        
        // 4. Recupera topologia di rete tramite il proxy del backend (consigliato)
        try {
          // Usa l'endpoint backend che fa da proxy e beneficia di retry/cache/logging
          const topologyUrl = `${API_ENDPOINTS.netflowTenants}${firstTenant.id}/topology/?minutes=60`;
          const topologyRes = await fetchWithAuth(topologyUrl);
          if (topologyRes && topologyRes.ok) {
            const topoData = await topologyRes.json();
            if (mounted) setTopologyData(topoData && topoData.nodes && topoData.nodes.length > 0 ? topoData : 'mock');
          } else {
            console.warn('[NetFlow] Topology proxy fetch failed:', topologyRes?.status);
          }
        } catch (e) {
          console.warn('[NetFlow] Errore nel recupero topologia (proxy):', e);
        }
        
        // 5. Recupera anomalie reali da NetFlow
        try {
          const anomaliesUrl = `${API_ENDPOINTS.netflowTenants}${firstTenant.id}/anomalies/?time_range=${range}`;
          const anomaliesRes = await fetchWithAuth(anomaliesUrl);
          if (anomaliesRes && anomaliesRes.ok) {
            const anomData = await anomaliesRes.json();
            
            // Verifica se anomData è un array o ha una proprietà 'anomalies'
            const anomaliesArray = Array.isArray(anomData) ? anomData : (anomData?.anomalies || []);
            
            if (mounted) {
              if (anomaliesArray.length > 0) {
                // Mappa i dati da NetFlow al formato del componente
                const mappedAnomalies = anomaliesArray.map((anom) => ({
                  id: anom.id || `anom-${Math.random().toString(36).substr(2, 9)}`,
                  type: anom.anomaly_type || anom.type || 'unknown',
                  severity: anom.severity?.toLowerCase() || 'medium',
                  source_ip: anom.source_ip || anom.src_addr,
                  destination_ip: anom.destination_ip || anom.dst_addr,
                  ml_score: anom.ml_score || anom.score || 0.5,
                  detected_at: anom.detected_at || anom.timestamp || new Date().toISOString(),
                  description: anom.description || `${anom.anomaly_type || 'Anomalia'} rilevata`
                }));
                setAnomalies(mappedAnomalies);
              } else {
                setAnomalies([]);
              }
            }
          } else {
            console.warn('[NetFlow] Anomalies fetch failed:', anomaliesRes?.status);
            if (mounted) setAnomalies([]);
          }
        } catch (e) {
          console.warn('[NetFlow] Errore nel recupero anomalie:', e);
          if (mounted) setAnomalies([]);
        }
      } catch (e) {
        console.error('[NetFlow] Errore nel caricamento dati NetFlow:', e);
        if (mounted) setIncidents([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    
    loadNetflowData();
    return () => {
      mounted = false;
    };
  }, [range, reloadCounter]); // Ricarica dati quando cambia il range temporale o si forza il reload
  
  // Handler per cambiare il range temporale
  const handleRangeChange = (newRange) => {
    // Forza reload anche se il range è uguale (migliora la reattività UI)
    setRange(newRange);
    setReloadCounter(c => c + 1);
    setLoading(true);
  };

  // Compute filtered and paginated incidents
  const filteredIncidents = incidents.filter(inc => {
    // Filtro priorità unificata (combina severity + ml_score)
    if (incidentFilters.priority !== 'all') {
      const p = computePriority(inc.severity, inc.ml_score);
      if (p.level !== incidentFilters.priority) return false;
    }
    
    // Filtro status: gestisce il caso speciale open_in_progress
    if (incidentFilters.status !== 'all') {
      if (incidentFilters.status === 'open_in_progress') {
        if (inc.status !== 'open' && inc.status !== 'in-progress') return false;
      } else if ((inc.status || '').toLowerCase() !== incidentFilters.status.toLowerCase()) {
        return false;
      }
    }
    
    return true;
  });

  const totalIncidentPages = Math.max(1, Math.ceil(filteredIncidents.length / INCIDENTS_PER_PAGE));
  const paginatedIncidents = filteredIncidents.slice((incidentPage - 1) * INCIDENTS_PER_PAGE, incidentPage * INCIDENTS_PER_PAGE);

  // Reset page when filters change
  useEffect(() => {
    setIncidentPage(1);
  }, [incidentFilters]);

  return (
    <div className="p-6 max-w-8xl mx-auto">
      {providerRangeUnsupported && (
        <div className="bg-orange-100 border border-orange-300 rounded-lg p-4 mb-4 text-center">
          <span className="text-orange-700 font-bold"><AlertTriangle size={14} className="inline" /> {t('networkingPage.providerRangeWarning')}</span>
        </div>
      )}
      {!hasNetworkingBundle ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <div className="text-3xl mb-4"><Lock size={40} className="mx-auto" /></div>
          <h2 className="text-2xl font-bold text-yellow-800 mb-2">{t('networkingPage.notAvailableTitle')}</h2>
          <p className="text-yellow-700 mb-4">
            {t('networkingPage.notAvailableBundle')} <strong>{t('networkingPage.networkingSurveillance')}</strong>.
          </p>
          <p className="text-yellow-600 text-sm mb-6">
            {t('networkingPage.contactDistributor')}
          </p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <div className="text-3xl mb-4"><AlertTriangle size={40} className="mx-auto text-red-500" /></div>
          <h2 className="text-2xl font-bold text-red-800 mb-2">{t('networkingPage.errorTitle')}</h2>
          <p className="text-red-700">{error}</p>
        </div>
      ) : (
        <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">{t('networkingPage.pageTitle')}</h1>
          <div className="text-sm text-gray-500">
            {t('networkingPage.pageSubtitle')}
            {loading && <span className="ml-3 inline-block"><><Hourglass size={14} className="inline animate-pulse" /> {t('networkingPage.loadingNetflow')}</></span>}
            {tenantData && <span className="ml-3 inline-block text-green-600"><Check size={14} className="inline" /> {t('networkingPage.liveData')}</span>}
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          <button
            onClick={() => setShowTopologyHistory(true)}
            className="px-3 py-1 rounded transition-colors bg-yellow-500 text-white hover:bg-yellow-600 flex items-center gap-1"
            title={t('networkingPage.topologyHistory')}
          >
            <><ScrollText size={14} className="inline" /> {t('networkingPage.historyBtn')}</>
          </button>
          <button
            onClick={() => { setReloadCounter(c => c + 1); setLoading(true); }}
            className={`px-3 py-1 rounded transition-colors ml-2 bg-yellow-500 text-white hover:bg-yellow-600`}
          >
            {t('networkingPage.refreshBtn')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-tour="networking-kpis">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">{t('networkingPage.flows')}</div>
            {dashboardData && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">LIVE</span>}
          </div>
          <div className="text-2xl font-bold">{totals.flows}</div>
          <div className="mt-2"><Sparkline values={sparkValues} /></div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">{t('networkingPage.anomaliesLabel')}</div>
            {dashboardData && <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">LIVE</span>}
          </div>
          <div className="text-2xl font-bold">{totals.anomalies}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">{t('networkingPage.anomalyRate')}</div>
          <div className="text-2xl font-bold">{totals.anomalyRate}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-sm text-gray-500">{t('networkingPage.sessionsLabel')}</div>
          <div className="text-2xl font-bold">{totals.sessions}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold mb-2 text-sm">{t('networkingPage.protocolDist')}</h3>
          <div className="space-y-1">
            {protocol.map((p) => (
              <div key={p.name} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: p.name === 'TCP' ? '#3b82f6' : p.name === 'UDP' ? '#10b981' : '#f97316' }} />
                  <span className="text-xs text-gray-700">{p.name}</span>
                </div>
                <div className="text-xs font-bold">{p.value}%</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {checkedBundle && hasNetworkingBundle && (
        <div className="bg-white rounded-lg shadow mb-6" data-tour="networking-topology">
          <div 
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
            onClick={() => setTopologyOpen(!topologyOpen)}
          >
            <h2 className="font-semibold flex items-center gap-2">
              <svg 
                className={`w-5 h-5 transition-transform ${topologyOpen ? 'rotate-90' : ''}`}
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a 1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              {t('networkingPage.networkTopology')}
              <button
                className="ml-4 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs border border-yellow-300 hover:bg-yellow-200"
                onClick={handleForceTopologyUpdate}
                title={t('networkingPage.updateTopology')}
              >
                {t('networkingPage.updateTopology')}
              </button>
            </h2>
            <div className="text-sm text-gray-500">
              {dashboardData ? t('networkingPage.liveDataLabel') : t('networkingPage.waitingNetflow')}
            </div>
          </div>
          {topologyOpen && (
            <div className="p-4 border-t">
              {/* Mostra topologia se: ci sono flows OPPURE se abbiamo dati di topologia con nodi */}
              {(dashboardData && dashboardData.total_flows > 0) || (topologyData && topologyData !== 'mock' && topologyData.nodes && topologyData.nodes.length > 0) ? (
                <TopologyDiagram 
                  incidentIPs={incidents.filter(i => i.status === 'open' || i.status === 'in-progress' || i.status === 'confermato').map(i => i.affected_ip).filter(Boolean)}
                  topologyData={topologyData}
                />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                  <svg className="w-24 h-24 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-lg font-semibold mb-2">{t('networkingPage.noNetworkData')}</p>
                  <p className="text-sm text-center max-w-md">
                    {t('networkingPage.topologyAutoDesc')}
                    <br />
                    <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded mt-2 inline-block">
                      {t('networkingPage.tenantLabel')} {tenantData?.remote_tenant_id || 'N/A'}
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <BarChart3 size={16} className="inline" /> {t('networkingPage.analyzerStatus')}
          </h3>
          <div className="space-y-1">
            <div className="text-sm text-gray-600">
              {t('networkingPage.statusLabel')} <span className={`font-semibold ${dashboardData?.status === 'HEALTHY' ? 'text-green-600' : 'text-red-600'}`}>
                {dashboardData?.status === 'HEALTHY' ? <><Check size={14} className="inline" /> {t('networkingPage.activeStatus')}</> : <><X size={14} className="inline" /> {t('networkingPage.offlineStatus')}</>}
              </span>
            </div>
            <div className="text-sm text-gray-600">
              {t('networkingPage.tenantLabel')} <span className="font-mono text-xs">{tenantData?.remote_tenant_id?.substring(0, 8) || 'N/A'}...</span>
            </div>
            <div className="text-sm text-gray-600">
              {t('networkingPage.lastUpdate')} <span className="font-medium">
                {dashboardData?.timestamp ? new Date(dashboardData.timestamp).toLocaleString('it-IT') : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <TrendingUp size={16} className="inline" /> {t('networkingPage.historicalAnalysis')}
          </h3>
          <div className="space-y-1">
            <div className="text-sm text-gray-600">
              {t('networkingPage.periodLabel')} <span className="font-medium">{rangeLabels[range]}</span>
            </div>
            <div className="text-sm text-gray-600">
              {t('networkingPage.analyzedFlows')} <span className="font-medium">{totals.flows}</span>
            </div>
            <div className="text-sm text-gray-600">
              {t('networkingPage.detectedAnomalies')} <span className={`font-medium ${totals.anomalies > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {totals.anomalies}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Gestione Incidenti Dashboard */}
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2"><Siren size={22} className="inline" /> {t('networkingPage.incidentManagement')}</h2>
            <p className="text-sm text-gray-500 mt-1">{t('networkingPage.incidentManagementDesc')}</p>
          </div>
          <div className="text-sm text-green-600 flex items-center gap-1">
            <span className="w-2 h-2 bg-green-600 rounded-full"></span>
            {t('networkingPage.realTimeUpdate')}
          </div>
        </div>

        {/* Filtri */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-2">{t('networkingPage.priorityFilterLabel')}</label>
              <select 
                value={incidentFilters.priority}
                onChange={(e) => setIncidentFilters({...incidentFilters, priority: e.target.value})}
                className="w-full bg-white text-gray-900 rounded px-3 py-2 text-sm border border-gray-300 hover:border-gray-400"
              >
                <option value="all">{t('networkingPage.allPriorities')}</option>
                <option value="critica">● {t('networkingPage.filterCritical')}</option>
                <option value="alta">● {t('networkingPage.filterHigh')}</option>
                <option value="media">● {t('networkingPage.filterMedium')}</option>
                <option value="bassa">● {t('networkingPage.filterLow')}</option>
                <option value="minima">● {t('networkingPage.filterMinimal')}</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-2">{t('networkingPage.statusFilterLabel')}</label>
              <select 
                value={incidentFilters.status}
                onChange={(e) => setIncidentFilters({...incidentFilters, status: e.target.value})}
                className="w-full bg-white text-gray-900 rounded px-3 py-2 text-sm border border-gray-300 hover:border-gray-400"
              >
                <option value="open_in_progress">{t('networkingPage.openAndInProgress')}</option>
                <option value="all">{t('networkingPage.allFilter')}</option>
                <option value="open">{t('networkingPage.onlyOpen')}</option>
                <option value="in-progress">{t('networkingPage.onlyInProgress')}</option>
                <option value="confermato">{t('networkingPage.confirmedThreat')}</option>
                <option value="falso positivo">{t('networkingPage.falsePositiveFilter')}</option>
                <option value="chiuso">{t('networkingPage.closedFilter')}</option>
              </select>
            </div>
          </div>
        </div>

        {/* Metriche */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
            <div className="text-sm font-semibold text-gray-700 mb-1">{t('networkingPage.openIncidents')}</div>
            <div className="text-3xl font-bold text-red-600">{incidents.filter(i => i.status === 'open').length}</div>
            <div className="text-xs text-gray-600 mt-2">
              <div><span className="text-red-600 font-semibold">● {incidents.filter(i => computePriority(i.severity, i.ml_score).level === 'critica').length}</span> {t('networkingPage.criticalPriority')}</div>
              <div><span className="text-orange-600 font-semibold">● {incidents.filter(i => computePriority(i.severity, i.ml_score).level === 'alta').length}</span> {t('networkingPage.highPriority')}</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
            <div className="text-sm font-semibold text-gray-700 mb-1">{t('networkingPage.inProgressLabel')}</div>
            <div className="text-3xl font-bold text-blue-600">{incidents.filter(i => i.status === 'in-progress').length}</div>
            <div className="text-xs text-gray-600 mt-1">{t('networkingPage.inAnalysis')}</div>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-gray-700 mb-1">{t('networkingPage.falsePositivesLabel')}</div>
            <div className="text-3xl font-bold text-emerald-600">{incidents.filter(i => i.status === 'falso positivo').length}</div>
            <div className="text-xs text-gray-600 mt-2">
              <div><span className="text-orange-600 font-semibold">● {incidents.filter(i => i.status === 'confermato').length}</span> {t('networkingPage.confirmedCount')}</div>
              <div><span className="text-gray-500 font-semibold">● {incidents.filter(i => i.status === 'chiuso').length}</span> {t('networkingPage.closedCount')}</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
            <div className="text-sm font-semibold text-gray-700 mb-1">{t('networkingPage.highPriorityPlus')}</div>
            <div className="text-3xl font-bold text-orange-600">
              {incidents.filter(i => { const p = computePriority(i.severity, i.ml_score); return p.level === 'critica' || p.level === 'alta'; }).length}
            </div>
            <div className="text-xs text-gray-600 mt-1">
              {t('networkingPage.needAttention')}
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
            <div className="text-sm font-semibold text-purple-700 mb-2">{t('networkingPage.riskTrend')}</div>
            <div className="w-full flex items-center justify-between">
              <div className="flex-1 pr-4">
                <div style={{ width: '100%', height: 48 }}>
                  <Sparkline values={mlTrend} color="#ef4444" stroke={2} width={260} height={48} />
                </div>
              </div>
              <div className="flex-none text-right">
                <div className="text-sm text-purple-600">{t('networkingPage.lastLabel')}</div>
                <div className="text-2xl font-bold text-purple-800">{mlDisplay}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Lista Incidenti - Card Layout */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">{t('networkingPage.incidentList')} ({filteredIncidents.length})</h3>
            <div className="text-xs text-gray-500">
              <><Lightbulb size={14} className="inline" /> {t('networkingPage.clickForDetails')}</>
            </div>
          </div>
          
          {filteredIncidents.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {paginatedIncidents.map(inc => {
                // === PRIORITÀ UNIFICATA ===
                // Un solo indicatore che combina severity + ml_score
                const priority = computePriority(inc.severity, inc.ml_score);

                // Threat names mapped to translation keys
                const threatNameKeys = {
                  'c2_beacon': 'networkingPage.threatC2',
                  'data_exfiltration': 'networkingPage.threatExfiltration',
                  'ssh_bruteforce': 'networkingPage.threatSSH',
                  'port_scan': 'networkingPage.threatPortScan',
                  'dns_tunneling': 'networkingPage.threatDNS',
                  'lateral_movement': 'networkingPage.threatLateral',
                  'malware_communication': 'networkingPage.threatMalware',
                  'unknown': 'networkingPage.threatUnknown'
                };
                const threatLabelKey = threatNameKeys[(inc.threat_type || '').toLowerCase()];
                const threatLabel = threatLabelKey ? t(threatLabelKey) : (inc.threat_type || t('networkingPage.threatUnknown'));

                // Threat context mapped to translation keys
                const threatContextKeys = {
                  'c2_beacon': 'networkingPage.threatC2Context',
                  'data_exfiltration': 'networkingPage.threatExfilContext',
                  'ssh_bruteforce': 'networkingPage.threatSSHContext',
                  'port_scan': 'networkingPage.threatPortScanContext',
                  'dns_tunneling': 'networkingPage.threatDNSContext',
                  'lateral_movement': 'networkingPage.threatLateralContext',
                  'malware_communication': 'networkingPage.threatMalwareContext'
                };
                const threatContextKey = threatContextKeys[(inc.threat_type || '').toLowerCase()];
                const threatContext = threatContextKey ? t(threatContextKey) : (inc.description || t('networkingPage.threatGenericContext'));

                // IP info
                const sourceIP = inc.source_ip || inc.affected_ip || '—';
                const destIP = inc.dest_ip || inc.destination_ip || null;

                // Status info
                const isOpen = inc.status === 'open';
                const isInProgress = inc.status === 'in-progress';
                const isResolved = ['confermato', 'falso positivo', 'chiuso'].includes(inc.status);

                return (
                  <div 
                    key={inc.id} 
                    className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer border-l-4 ${
                      isResolved ? 'border-l-gray-300 opacity-75' :
                      priority.level === 'critica' ? 'border-l-red-500' :
                      priority.level === 'alta' ? 'border-l-orange-500' :
                      priority.level === 'media' ? 'border-l-amber-500' :
                      priority.level === 'bassa' ? 'border-l-blue-400' :
                      'border-l-green-400'
                    }`}
                    onClick={() => setSelectedIncident(inc)}
                  >
                    {/* Riga 1: Header con ID, badges, e azioni */}
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-semibold text-gray-800">{inc.id}</span>
                        <PriorityBadge severity={inc.severity} mlScore={inc.ml_score} />
                        <StatusBadge status={inc.status} />
                        {(inc.is_repeat_offender || inc.details?.ip_history?.is_repeat_offender) && (
                          <RepeatOffenderBadge ipHistory={inc.details?.ip_history || { is_repeat_offender: true, src_ip_incidents_30d: inc.repeat_offender_count }} />
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-xs text-gray-400">
                          {new Date(inc.opened_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <button 
                          onClick={(e) => { e.stopPropagation(); setSelectedIncident(inc); }}
                          className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                        >
                          {t('networkingPage.openDetailsBtn')}
                        </button>
                      </div>
                    </div>
                    
                    {/* Riga 2: Indicatore di Priorità + Tipo minaccia + contesto */}
                    <div className="flex items-start gap-3 mb-2">
                      {/* Semaforo priorità */}
                      <div className={`flex-shrink-0 rounded-lg p-2 border ${priority.bg} text-center min-w-[90px]`}>
                        <div className="text-xl mb-0.5">{priority.icon}</div>
                        <div className={`text-xs font-bold ${priority.color}`}>{t(priority.shortLabel)}</div>
                      </div>
                      
                      {/* Info minaccia */}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
                          <span>{threatNameKeys[(inc.threat_type || '').toLowerCase()] ? (inc.threat_type === 'c2_beacon' ? <Radio size={16} /> : inc.threat_type === 'data_exfiltration' ? <Upload size={16} /> : inc.threat_type === 'ssh_bruteforce' ? <KeyRound size={16} /> : inc.threat_type === 'port_scan' ? <Search size={16} /> : <AlertTriangle size={16} />) : <AlertTriangle size={16} />}</span>
                          {threatLabel}
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed mb-2">{threatContext}</p>
                        
                        {/* IP coinvolti - sempre mostrati con indirizzo */}
                        <div className="flex items-center gap-4 text-xs">
                          <div className="flex items-center gap-1.5">
                            <span className="text-gray-400">Da:</span>
                            <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-800 font-medium">{sourceIP}</span>
                          </div>
                          {destIP && (
                            <>
                              <span className="text-gray-300">→</span>
                              <div className="flex items-center gap-1.5">
                                <span className="text-gray-400">Verso:</span>
                                <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-gray-800 font-medium">{destIP}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Riga 3: Spiegazione sintetica del verdetto AI */}
                    <div className={`rounded-lg px-3 py-2 text-sm flex items-center gap-2 ${priority.bg}`}>
                      <span className="text-sm"><Bot size={14} className="inline" /></span>
                      <span className={`${priority.color} font-medium`}>Verdetto AI:</span>
                      <span className="text-gray-700">{t(priority.explanation)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-6 py-12 text-center text-gray-500">
              <div className="text-5xl mb-4"><CheckCircle2 size={48} className="mx-auto text-emerald-500" /></div>
              <p className="text-lg font-semibold text-gray-700">{t('networkingPage.noIncidents')}</p>
              <p className="text-sm mt-1">{t('networkingPage.networkSafe')}</p>
            </div>
          )}
        </div>

        {/* Paginazione */}
        {filteredIncidents.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 text-sm text-gray-600 flex items-center justify-between">
            <div>
              <button
                onClick={() => setIncidentPage(p => Math.max(1, p - 1))}
                disabled={incidentPage <= 1}
                className={`px-3 py-1 mr-2 rounded border ${incidentPage <= 1 ? 'text-gray-400 border-gray-200' : 'text-blue-600 border-gray-300 hover:bg-gray-50'}`}>
                {t('networkingPage.previousPage')}
              </button>
              <button
                onClick={() => setIncidentPage(p => Math.min(totalIncidentPages, p + 1))}
                disabled={incidentPage >= totalIncidentPages}
                className={`px-3 py-1 rounded border ${incidentPage >= totalIncidentPages ? 'text-gray-400 border-gray-200' : 'text-blue-600 border-gray-300 hover:bg-gray-50'}`}>
                {t('networkingPage.nextPage')}
              </button>
            </div>
            <div className="text-right">{t('networkingPage.pageOf', { current: incidentPage, total: totalIncidentPages })}</div>
          </div>
        )}
      </div>
      
      {/* Anomalie Rilevate */}
      {anomalies.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4"><AlertTriangle size={20} className="inline text-yellow-600" /> {t('networkingPage.networkAnomalies')}</h2>
          <div className="space-y-3">
            {anomalies.map(anom => (
              <div key={anom.id} className="border-l-4 border-yellow-500 bg-yellow-50 p-4 rounded">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-semibold text-sm">{anom.type?.replace('_', ' ').toUpperCase()}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        anom.severity === 'critical' ? 'bg-red-600 text-white' :
                        anom.severity === 'high' ? 'bg-orange-500 text-white' :
                        anom.severity === 'medium' ? 'bg-yellow-500 text-white' :
                        'bg-blue-500 text-white'
                      }`}>
                        {anom.severity.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 mb-1">
                      {anom.description}
                    </div>
                    <div className="text-xs text-gray-600">
                      Da: <span className="font-mono">{anom.source_ip}</span>
                      {anom.destination_ip && (
                        <> → Verso: <span className="font-mono">{anom.destination_ip}</span></>
                      )}
                      {' • '}
                      Rischio: <span className="font-semibold">{(anom.ml_score * 100).toFixed(0)}%</span>
                      {' • '}
                      Rilevata: {new Date(anom.detected_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </>
      )}
      {/* Bottone di test fisso per debug eventi globali */}
      {/* <button style={{position:'fixed',top:10,right:10,zIndex:99999,background:'#f00',color:'#fff',padding:'16px',borderRadius:'8px',fontWeight:'bold',fontSize:'18px'}} onClick={()=>alert('TEST BUTTON CLICKED')}>TEST EVENTO</button> */}

      {selectedIncident && (
        <IncidentDetailModal 
          incident={selectedIncident}
          onClose={() => setSelectedIncident(null)}
          onUpdateStatus={handleUpdateStatus}
          onAddComment={handleAddComment}
        />
      )}

      {/* Topology History Panel */}
      <TopologyHistoryPanel 
        tenantId={tenantData?.id}
        isOpen={showTopologyHistory}
        onClose={() => setShowTopologyHistory(false)}
        onSelectSnapshot={(snapshot) => {
          // Carica lo snapshot selezionato nella topologia
          if (snapshot.nodes && snapshot.edges) {
            setTopologyData({ nodes: snapshot.nodes, edges: snapshot.edges });
          }
        }}
      />
    </div>
  );
}

// ============ INCIDENTS PANEL COMPONENTS ============

// ===== SISTEMA PRIORITÀ UNIFICATO =====
// Combina severity (gravità intrinseca della minaccia) e ml_score (certezza AI)
// in un UNICO indicatore coerente mostrato ovunque.
// Matrice:
//              ml<40%    ml 40-65%   ml 65-80%   ml>=80%
// critical:    media     alta        critica     critica
// high:        bassa     media       alta        critica
// medium:      minima    bassa       media       alta
// low:         minima    minima      bassa       media
function computePriority(severity, mlScore) {
  const sev = (severity || 'medium').toLowerCase();
  const mlPct = Math.round((mlScore || 0) * 100);
  
  // Matrice 2D: [severity][mlRange] => priority level
  const matrix = {
    critical: mlPct >= 65 ? 'critica' : mlPct >= 40 ? 'alta' : 'media',
    high:     mlPct >= 80 ? 'critica' : mlPct >= 65 ? 'alta' : mlPct >= 40 ? 'media' : 'bassa',
    medium:   mlPct >= 80 ? 'alta' : mlPct >= 65 ? 'media' : mlPct >= 40 ? 'bassa' : 'minima',
    low:      mlPct >= 80 ? 'media' : mlPct >= 65 ? 'bassa' : 'minima'
  };
  const level = matrix[sev] || matrix.medium;
  
  const configs = {
    critica: {
      level: 'critica', label: 'networkingPage.priorityCriticalLabel', shortLabel: 'networkingPage.priorityCriticalShort',
      icon: <Circle size={14} className="fill-red-500 text-red-500" />, color: 'text-red-700', bg: 'bg-red-50 border-red-200',
      badgeBg: 'bg-red-100', badgeText: 'text-red-800', dot: 'bg-red-500',
      explanation: 'networkingPage.priorityCriticalExpl',
      panelBg: 'bg-red-50', panelBorder: 'border-red-200', panelText: 'text-red-800'
    },
    alta: {
      level: 'alta', label: 'networkingPage.priorityHighLabel', shortLabel: 'networkingPage.priorityHighShort',
      icon: <Circle size={14} className="fill-orange-500 text-orange-500" />, color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200',
      badgeBg: 'bg-orange-100', badgeText: 'text-orange-800', dot: 'bg-orange-500',
      explanation: 'networkingPage.priorityHighExpl',
      panelBg: 'bg-orange-50', panelBorder: 'border-orange-200', panelText: 'text-orange-800'
    },
    media: {
      level: 'media', label: 'networkingPage.priorityMediumLabel', shortLabel: 'networkingPage.priorityMediumShort',
      icon: <Circle size={14} className="fill-amber-500 text-amber-500" />, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200',
      badgeBg: 'bg-amber-100', badgeText: 'text-amber-800', dot: 'bg-amber-500',
      explanation: 'networkingPage.priorityMediumExpl',
      panelBg: 'bg-yellow-50', panelBorder: 'border-yellow-200', panelText: 'text-yellow-800'
    },
    bassa: {
      level: 'bassa', label: 'networkingPage.priorityLowLabel', shortLabel: 'networkingPage.priorityLowShort',
      icon: <Circle size={14} className="fill-blue-500 text-blue-500" />, color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200',
      badgeBg: 'bg-blue-100', badgeText: 'text-blue-800', dot: 'bg-blue-500',
      explanation: 'networkingPage.priorityLowExpl',
      panelBg: 'bg-blue-50', panelBorder: 'border-blue-200', panelText: 'text-blue-800'
    },
    minima: {
      level: 'minima', label: 'networkingPage.priorityMinimalLabel', shortLabel: 'networkingPage.priorityMinimalShort',
      icon: <Circle size={14} className="fill-green-500 text-green-500" />, color: 'text-green-700', bg: 'bg-green-50 border-green-200',
      badgeBg: 'bg-green-100', badgeText: 'text-green-800', dot: 'bg-green-500',
      explanation: 'networkingPage.priorityMinimalExpl',
      panelBg: 'bg-green-50', panelBorder: 'border-green-200', panelText: 'text-green-800'
    }
  };
  return configs[level];
}

// PriorityBadge — l'UNICO badge di classificazione mostrato agli utenti
function PriorityBadge({ severity, mlScore, size = 'sm' }) {
  const { t } = useTranslation();
  const p = computePriority(severity, mlScore);
  if (size === 'lg') {
    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold ${p.badgeBg} ${p.badgeText}`}>
        <span>{p.icon}</span>
        {t(p.label)}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${p.badgeBg} ${p.badgeText}`}>
      <span className={`w-2 h-2 rounded-full ${p.dot}`}></span>
      {t(p.shortLabel)}
    </span>
  );
}

// Severity badge component — usato SOLO nella vista tecnica avanzata
function SeverityBadge({ severity }) {
  const { t } = useTranslation();
  const config = {
    critical: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500', label: t('networkingPage.severityCritical') },
    high: { bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500', label: t('networkingPage.severityHigh') },
    medium: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500', label: t('networkingPage.severityMedium') },
    low: { bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500', label: t('networkingPage.severityLow') },
    info: { bg: 'bg-gray-100', text: 'text-gray-800', dot: 'bg-gray-500', label: t('networkingPage.severityInfo') }
  };
  const style = config[severity] || config.info;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}>
      <span className={`w-2 h-2 rounded-full ${style.dot}`}></span>
      {style.label}
    </span>
  );
}

// Status badge component
function StatusBadge({ status, size = 'sm' }) {
  const { t } = useTranslation();
  const normalizedStatus = (status || 'open').toLowerCase().trim();
  const config = {
    open: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: t('networkingPage.statusOpen'), icon: <Circle size={12} className="fill-red-500 text-red-500" />, dot: 'bg-red-500' },
    'in-progress': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: t('networkingPage.statusInProg'), icon: <Circle size={12} className="fill-blue-500 text-blue-500" />, dot: 'bg-blue-500' },
    confermato: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-300', label: t('networkingPage.statusConfirmed'), icon: <AlertTriangle size={12} className="text-orange-500" />, dot: 'bg-orange-500' },
    'falso positivo': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300', label: t('networkingPage.statusFP'), icon: <CheckCircle2 size={12} className="text-emerald-500" />, dot: 'bg-emerald-500' },
    chiuso: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', label: t('networkingPage.statusClosed'), icon: <Circle size={12} className="fill-gray-500 text-gray-500" />, dot: 'bg-gray-500' }
  };
  const style = config[normalizedStatus] || config.open;
  
  if (size === 'lg') {
    return (
      <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 text-sm font-bold ${style.bg} ${style.text} ${style.border} shadow-sm`}>
        <span className={`w-2.5 h-2.5 rounded-full ${style.dot} animate-pulse`}></span>
        {style.label}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs font-medium ${style.bg} ${style.text} ${style.border}`}>
      <span className={`w-2 h-2 rounded-full ${style.dot}`}></span>
      {style.label}
    </span>
  );
}

// Source badge component (simulator vs production)
function SourceBadge({ source }) {
  const { t } = useTranslation();
  if (source === 'simulator') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
        </svg>
        {t('networkingPage.simulation')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
      {t('networkingPage.production')}
    </span>
  );
}

// ============ NUOVI COMPONENTI v2.0 ============

// IP Classification Badge - mostra tipo IP con icona
function IPClassificationBadge({ classification }) {
  const { t } = useTranslation();
  // Se non c'è classificazione, mostra un placeholder
  if (!classification) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-gray-500 border border-gray-200 text-xs">
        <HelpCircle size={14} className="inline" />
        <span>{t('networkingPage.notClassified')}</span>
      </span>
    );
  }
  
  const { ip_type, ip_type_info, confidence, signals, is_internal } = classification;
  const icon = ip_type_info?.icon || <HelpCircle size={14} className="inline" />;
  const name = ip_type_info?.name || ip_type || 'Unknown';
  const category = ip_type_info?.category || 'unknown';
  
  // Colori in base alla categoria
  const categoryColors = {
    internal: 'bg-blue-50 text-blue-700 border-blue-200',
    cloud: 'bg-purple-50 text-purple-700 border-purple-200',
    infrastructure: 'bg-green-50 text-green-700 border-green-200',
    application: 'bg-pink-50 text-pink-700 border-pink-200',
    security: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    backbone: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    network: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    entertainment: 'bg-orange-50 text-orange-700 border-orange-200',
    business: 'bg-teal-50 text-teal-700 border-teal-200',
    unknown: 'bg-gray-50 text-gray-600 border-gray-200'
  };
  
  const colorClass = categoryColors[category] || categoryColors.unknown;
  const confidenceDot = confidence === 'high' ? '●' : confidence === 'medium' ? '◐' : '○';
  
  return (
    <span 
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs font-medium ${colorClass} cursor-help`}
      title={`Tipo: ${name}\nConfidence: ${confidence || 'N/A'}\nSignals: ${(signals || []).join(', ') || 'N/A'}`}
    >
      <span>{icon}</span>
      <span>{name}</span>
      {confidence && <span className="text-[10px] opacity-60">{confidenceDot}</span>}
    </span>
  );
}

// MITRE ATT&CK Badge - link alla tecnica MITRE
function MitreBadge({ technique, name, category }) {
  if (!technique) return null;
  
  const mitreUrl = `https://attack.mitre.org/techniques/${technique.replace('.', '/')}/`;
  
  return (
    <a 
      href={mitreUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-red-50 text-red-700 border border-red-200 text-xs font-medium hover:bg-red-100 transition-colors"
      title={`${name}\nCategoria: ${category || 'N/A'}\nClicca per vedere su MITRE ATT&CK`}
    >
      <Target size={14} className="inline" />
      <span className="font-mono">{technique}</span>
      {name && <span className="text-red-600 max-w-[150px] truncate">• {name}</span>}
    </a>
  );
}

// Repeat Offender Badge - warning per IP recidivi
function RepeatOffenderBadge({ ipHistory }) {
  if (!ipHistory) return null;
  
  const { is_repeat_offender, is_repeat_target, src_ip_incidents_30d, dst_ip_incidents_30d } = ipHistory;
  
  if (!is_repeat_offender && !is_repeat_target) return null;
  
  return (
    <span 
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-300 text-xs font-semibold animate-pulse"
      title={`Incidenti ultimi 30gg:\n- IP Sorgente: ${src_ip_incidents_30d || 0}\n- IP Destinazione: ${dst_ip_incidents_30d || 0}`}
    >
      <AlertTriangle size={12} className="inline" />
      {is_repeat_offender && <span>RECIDIVO</span>}
      {is_repeat_offender && is_repeat_target && <span>+</span>}
      {is_repeat_target && <span>TARGET</span>}
      <span className="font-mono text-[10px]">({src_ip_incidents_30d || 0})</span>
    </span>
  );
}

// Baseline Badge - indica se comunicazione è nota o nuova
function BaselineBadge({ baseline }) {
  const { t } = useTranslation();
  // Mostra sempre qualcosa, anche se baseline è null
  if (!baseline) {
    return (
      <div className="text-xs text-gray-400 italic">
        {t('networkingPage.baselineNotAvailable')}
      </div>
    );
  }
  
  const { 
    is_established_pattern, 
    historical_connections = 0, 
    historical_bytes = 0,
    first_seen,
    last_seen 
  } = baseline;
  
  // Formatta bytes in formato leggibile
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Formatta data in formato italiano
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateStr;
    }
  };
  
  return (
    <div className="flex flex-col gap-2">
      {/* Badge principale */}
      <div className="flex items-center gap-3">
        {is_established_pattern ? (
          <span 
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-800 border border-green-300 text-xs font-medium"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>{t('networkingPage.usualCommunication')}</span>
          </span>
        ) : (
          <span 
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300 text-xs font-medium"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>{t('networkingPage.firstConnection')}</span>
          </span>
        )}
      </div>
      
      {/* Dettagli statistici */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <div className="bg-gray-50 rounded p-2">
          <div className="text-gray-500 mb-0.5">{t('networkingPage.historicalConnections')}</div>
          <div className="font-semibold text-gray-800">{historical_connections.toLocaleString('it-IT')}</div>
        </div>
        <div className="bg-gray-50 rounded p-2">
          <div className="text-gray-500 mb-0.5">{t('networkingPage.historicalTraffic')}</div>
          <div className="font-semibold text-gray-800">{formatBytes(historical_bytes)}</div>
        </div>
        {first_seen && (
          <div className="bg-gray-50 rounded p-2">
            <div className="text-gray-500 mb-0.5">{t('networkingPage.firstCommunication')}</div>
            <div className="font-semibold text-gray-800">{formatDate(first_seen)}</div>
          </div>
        )}
        {last_seen && (
          <div className="bg-gray-50 rounded p-2">
            <div className="text-gray-500 mb-0.5">{t('networkingPage.lastCommunication')}</div>
            <div className="font-semibold text-gray-800">{formatDate(last_seen)}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// IP Reputation Badge (semaforo)
function IPReputationBadge({ recommendation, confidence }) {
  const { t } = useTranslation();
  const config = {
    'ALLOW': { bg: 'bg-green-100', text: 'text-green-800', icon: <Circle size={12} className="fill-green-500 text-green-500" />, label: t('networkingPage.reputationSafe') },
    'BLOCK': { bg: 'bg-red-100', text: 'text-red-800', icon: <Circle size={12} className="fill-red-500 text-red-500" />, label: t('networkingPage.reputationBlock') },
    'NEUTRAL': { bg: 'bg-gray-100', text: 'text-gray-700', icon: <Circle size={12} className="fill-amber-500 text-amber-500" />, label: t('networkingPage.reputationNeutral') }
  };
  
  const style = config[recommendation] || config.NEUTRAL;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
      <span>{style.icon}</span>
      <span>{style.label}</span>
      {confidence && <span className="opacity-60">({(confidence * 100).toFixed(0)}%)</span>}
    </span>
  );
}

// IP Intelligence Lookup Component - FEATURE DISABILITATA (endpoint backend non implementato)
// TODO: Implementare endpoint /api/ordini/ip-intelligence/lookup/ nel backend
function IPIntelligenceLookup({ ip }) {
  const { t } = useTranslation();
  // IP interni: mostra badge "IP Interno"
  if (ip && (ip.startsWith('10.') || ip.startsWith('192.168.') || ip.startsWith('172.16.') || ip.startsWith('172.17.') || ip.startsWith('172.18.') || ip.startsWith('172.19.') || ip.startsWith('172.2') || ip.startsWith('172.30.') || ip.startsWith('172.31.'))) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-600">
        <Home size={14} className="inline" /> {t('networkingPage.internalIP')}
      </span>
    );
  }
  
  // Per IP esterni, non mostrare nulla (feature non disponibile)
  // Quando l'endpoint backend sarà disponibile, riabilitare il pulsante "Verifica Reputazione"
  return null;
}

// ============ NUOVI COMPONENTI USER-FRIENDLY v3.0 ============

// Educational Tooltip Component - per spiegare termini tecnici
function EducationalTooltip({ term, children }) {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = React.useState(false);
  
  const explanations = {
    'c2_beacon': {
      title: t('networkingPage.tooltipC2Title'),
      simple: t('networkingPage.tooltipC2Simple'),
      analogy: <><Home size={14} className="inline" /> {t('networkingPage.tooltipC2Analogy')}</>,
      risk: t('networkingPage.tooltipC2Risk')
    },
    'data_exfiltration': {
      title: t('networkingPage.tooltipExfilTitle'),
      simple: t('networkingPage.tooltipExfilSimple'),
      analogy: <><Package size={14} className="inline" /> {t('networkingPage.tooltipExfilAnalogy')}</>,
      risk: t('networkingPage.tooltipExfilRisk')
    },
    'bruteforce': {
      title: t('networkingPage.tooltipBFTitle'),
      simple: t('networkingPage.tooltipBFSimple'),
      analogy: <><KeyRound size={14} className="inline" /> {t('networkingPage.tooltipBFAnalogy')}</>,
      risk: t('networkingPage.tooltipBFRisk')
    },
    'ml_score': {
      title: t('networkingPage.tooltipMLTitle'),
      simple: t('networkingPage.tooltipMLSimple'),
      analogy: <><Target size={14} className="inline" /> {t('networkingPage.tooltipMLAnalogy')}</>,
      risk: t('networkingPage.tooltipMLRisk')
    },
    'false_positive': {
      title: t('networkingPage.tooltipFPTitle'),
      simple: t('networkingPage.tooltipFPSimple'),
      analogy: <><Siren size={14} className="inline" /> {t('networkingPage.tooltipFPAnalogy')}</>,
      risk: t('networkingPage.tooltipFPRisk')
    },
    'mitre': {
      title: t('networkingPage.tooltipMitreTitle'),
      simple: t('networkingPage.tooltipMitreSimple'),
      analogy: <><BookOpen size={14} className="inline" /> {t('networkingPage.tooltipMitreAnalogy')}</>,
      risk: t('networkingPage.tooltipMitreRisk')
    },
    'internal_ip': {
      title: t('networkingPage.tooltipInternalTitle'),
      simple: t('networkingPage.tooltipInternalSimple'),
      analogy: <><Building size={14} className="inline" /> {t('networkingPage.tooltipInternalAnalogy')}</>,
      risk: t('networkingPage.tooltipInternalRisk')
    },
    'external_ip': {
      title: t('networkingPage.tooltipExternalTitle'),
      simple: t('networkingPage.tooltipExternalSimple'),
      analogy: <><Globe size={14} className="inline" /> {t('networkingPage.tooltipExternalAnalogy')}</>,
      risk: t('networkingPage.tooltipExternalRisk')
    }
  };
  
  const explanation = explanations[term];
  if (!explanation) return children;
  
  return (
    <div className="relative inline-block">
      <span 
        className="cursor-help border-b border-dotted border-gray-400"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {children}
        <span className="ml-1 text-blue-500 text-xs">ⓘ</span>
      </span>
      {showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 p-4 text-left">
          <div className="font-semibold text-gray-900 mb-2">{explanation.title}</div>
          <div className="text-sm text-gray-700 mb-3">{explanation.simple}</div>
          <div className="bg-blue-50 rounded-lg p-2 mb-2">
            <div className="text-xs text-blue-600 font-medium">Analogia:</div>
            <div className="text-sm text-blue-800">{explanation.analogy}</div>
          </div>
          <div className="text-xs text-gray-500">
            <strong>Rischio:</strong> {explanation.risk}
          </div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
            <div className="border-8 border-transparent border-t-white"></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ML Recommendation Panel - pannello che spiega cosa fare in linguaggio semplice
// NOTA: NON mostra le percentuali grezze di ml_score e ml_confidence perché
// confondono gli utenti non tecnici (sembrano contraddittorie).
// Invece sintetizza un verdetto chiaro basato sul tipo di raccomandazione.
function MLRecommendationPanel({ analysis, details }) {
  const { t } = useTranslation();
  if (!analysis?.ml_recommendation) return null;
  
  const { type, icon, confidence_label } = analysis.ml_recommendation;
  
  // Configurazione colori, stili e spiegazioni per tipo di raccomandazione
  // Le spiegazioni sono generate lato frontend per evitare testi tecnici/contraddittori dall'API
  const typeConfig = {
    'likely_false_positive': {
      bgGradient: 'from-green-50 to-emerald-50',
      border: 'border-green-200',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      titleColor: 'text-green-800',
      descColor: 'text-green-700',
      title: t('networkingPage.mlFPTitle'),
      titleIcon: <CheckCircle2 size={20} className="text-green-600" />,
      description: t('networkingPage.mlFPDesc'),
      actionText: t('networkingPage.mlFPAction'),
      actionIcon: <ThumbsUp size={16} className="inline" />,
      confidenceDesc: t('networkingPage.mlFPConfidence')
    },
    'needs_investigation': {
      bgGradient: 'from-amber-50 to-yellow-50',
      border: 'border-amber-200',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      titleColor: 'text-amber-800',
      descColor: 'text-amber-700',
      title: t('networkingPage.mlInvestTitle'),
      titleIcon: <AlertTriangle size={20} className="text-amber-600" />,
      description: t('networkingPage.mlInvestDesc'),
      actionText: t('networkingPage.mlInvestAction'),
      actionIcon: <Search size={16} className="inline" />,
      confidenceDesc: t('networkingPage.mlInvestConfidence')
    },
    'likely_threat': {
      bgGradient: 'from-red-50 to-rose-50',
      border: 'border-red-200',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      titleColor: 'text-red-800',
      descColor: 'text-red-700',
      title: t('networkingPage.mlThreatTitle'),
      titleIcon: <Siren size={20} className="text-red-600" />,
      description: t('networkingPage.mlThreatDesc'),
      actionText: t('networkingPage.mlThreatAction'),
      actionIcon: <Zap size={16} className="inline" />,
      confidenceDesc: t('networkingPage.mlThreatConfidence')
    }
  };
  
  const config = typeConfig[type] || typeConfig['needs_investigation'];

  // Barra di certezza basata sul tipo (non sulla percentuale grezza)
  const confidenceWidth = type === 'likely_false_positive' ? 85 : type === 'likely_threat' ? 90 : 50;
  const confidenceBarColor = type === 'likely_false_positive' ? 'bg-green-500' : type === 'likely_threat' ? 'bg-red-500' : 'bg-amber-500';
  
  return (
    <div className={`bg-gradient-to-r ${config.bgGradient} border ${config.border} rounded-xl p-5 shadow-sm`}>
      {/* Header con verdetto chiaro */}
      <div className="flex items-start gap-4">
        <div className={`flex-shrink-0 w-14 h-14 ${config.iconBg} rounded-full flex items-center justify-center`}>
          <span className="text-3xl">{icon || config.titleIcon || config.title.charAt(0)}</span>
        </div>
        <div className="flex-1">
          <h3 className={`text-xl font-bold ${config.titleColor} mb-1`}>
            {config.title}
          </h3>
          <p className={`${config.descColor} text-sm leading-relaxed`}>
            {config.description}
          </p>
        </div>
      </div>
      
      {/* Indicatore di certezza semplificato */}
      <div className="mt-4 mb-3">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-gray-500">{t('networkingPage.aiCertaintyQuestion')}</span>
          <span className="font-medium">{config.confidenceDesc}</span>
        </div>
        <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full ${confidenceBarColor} rounded-full transition-all duration-500`}
            style={{ width: `${confidenceWidth}%` }}
          ></div>
        </div>
      </div>
      
      {/* Come funziona - mini-spiegazione educativa */}
      <div className="mt-3 p-3 rounded-lg bg-white/40 text-xs text-gray-500 leading-relaxed">
        <span className="font-medium"><Lightbulb size={12} className="inline" /> {t('networkingPage.howAIWorks')}</span> {t('networkingPage.howAIWorksDesc')}
      </div>
      
      {/* Suggerimento azione */}
      <div className={`mt-3 p-3 rounded-lg bg-white/60 border ${config.border}`}>
        <div className="flex items-center gap-2">
          <span className="text-lg">{config.actionIcon}</span>
          <span className={`text-sm font-medium ${config.descColor}`}>{config.actionText}</span>
        </div>
      </div>
    </div>
  );
}

// Threat Info Panel - spiega il tipo di minaccia in linguaggio semplice
function ThreatInfoPanel({ threatInfo, threatType, severity, mlScore }) {
  const { t } = useTranslation();
  // Priorità unificata per colori e label
  const priority = computePriority(severity, mlScore);
  
  // Spiegazioni user-friendly per ogni tipo di minaccia
  const threatExplanations = {
    'c2_beacon': {
      icon: <Radio size={20} />,
      whatIs: t('networkingPage.threatC2What'),
      whyBad: t('networkingPage.threatC2Why'),
      whatToDo: [
        t('networkingPage.threatC2Action1'),
        t('networkingPage.threatC2Action2'),
        t('networkingPage.threatC2Action3')
      ],
      commonCauses: [t('networkingPage.threatC2Cause1'), t('networkingPage.threatC2Cause2'), t('networkingPage.threatC2Cause3')]
    },
    'data_exfiltration': {
      icon: <Upload size={20} />,
      whatIs: t('networkingPage.threatExfilWhat'),
      whyBad: t('networkingPage.threatExfilWhy'),
      whatToDo: [
        t('networkingPage.threatExfilAction1'),
        t('networkingPage.threatExfilAction2'),
        t('networkingPage.threatExfilAction3')
      ],
      commonCauses: [t('networkingPage.threatExfilCause1'), t('networkingPage.threatExfilCause2'), t('networkingPage.threatExfilCause3')]
    },
    'ssh_bruteforce': {
      icon: <KeyRound size={20} />,
      whatIs: t('networkingPage.threatSSHWhat'),
      whyBad: t('networkingPage.threatSSHWhy'),
      whatToDo: [
        t('networkingPage.threatSSHAction1'),
        t('networkingPage.threatSSHAction2'),
        t('networkingPage.threatSSHAction3')
      ],
      commonCauses: [t('networkingPage.threatSSHCause1'), t('networkingPage.threatSSHCause2'), t('networkingPage.threatSSHCause3')]
    }
  };
  
  // Usa le spiegazioni specifiche o genera una generica
  const explanation = threatExplanations[threatType] || {
    icon: <AlertTriangle size={20} />,
    whatIs: threatInfo?.description || t('networkingPage.threatGenericWhat'),
    whyBad: threatInfo?.risk || t('networkingPage.threatGenericWhy'),
    whatToDo: [t('networkingPage.threatGenericAction1'), t('networkingPage.threatGenericAction2'), t('networkingPage.threatGenericAction3')],
    commonCauses: [t('networkingPage.threatGenericCause1'), t('networkingPage.threatGenericCause2'), t('networkingPage.threatGenericCause3')]
  };
  
  return (
    <div className={`${priority.panelBg} border ${priority.panelBorder} rounded-xl overflow-hidden`}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/50 bg-white/30">
        <div className="flex items-center gap-3">
          <span className="text-4xl">{explanation.icon}</span>
          <div>
            <h3 className={`text-lg font-bold ${priority.panelText}`}>
              {threatInfo?.name || t('networkingPage.suspiciousActivity')}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm">{priority.icon}</span>
              <span className={`text-sm font-medium ${priority.panelText}`}>
                {t(priority.label)}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Corpo */}
      <div className="p-5 space-y-4">
        {/* Cosa significa */}
        <div>
          <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
            <HelpCircle size={16} className="inline" /> {t('networkingPage.whatItMeans')}
          </h4>
          <p className="text-gray-700 text-sm leading-relaxed pl-6">
            {explanation.whatIs}
          </p>
        </div>
        
        {/* Perché è importante */}
        <div>
          <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
            <Zap size={16} className="inline" /> {t('networkingPage.whyImportant')}
          </h4>
          <p className="text-gray-700 text-sm leading-relaxed pl-6">
            {explanation.whyBad}
          </p>
        </div>
        
        {/* Cosa fare */}
        <div>
          <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
            <CheckCircle2 size={16} className="inline text-emerald-600" /> {t('networkingPage.whatShouldYouDo')}
          </h4>
          <ul className="space-y-2 pl-6">
            {explanation.whatToDo.map((action, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-green-500 mt-0.5">•</span>
                <span>{action}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Possibili cause legittime */}
        {explanation.commonCauses && (
          <div className="bg-white/50 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              <><Lightbulb size={12} className="inline" /> {t('networkingPage.legitimateCauses')}</>
            </h4>
            <div className="flex flex-wrap gap-2">
              {explanation.commonCauses.map((cause, idx) => (
                <span key={idx} className="px-2 py-1 bg-white rounded text-xs text-gray-600 border border-gray-200">
                  {cause}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Quick Summary Card - riassunto visivo immediato
// NON mostra percentuali grezze ma solo verdetti sintetizzati per coerenza con la lista
function QuickSummaryCard({ incident, analysis, enrichedDetails }) {
  const { t } = useTranslation();
  // Determina il "verdetto" basato sull'analisi ML
  const getVerdict = () => {
    if (!analysis?.ml_recommendation) return 'unknown';
    const { type } = analysis.ml_recommendation;
    if (type === 'likely_false_positive') return 'safe';
    if (type === 'likely_threat') return 'danger';
    return 'warning';
  };
  
  // Usa il sistema di priorità unificato
  const priority = computePriority(incident?.severity, incident?.ml_score);
  
  const verdict = getVerdict();
  const verdictConfig = {
    safe: {
      bg: 'bg-gradient-to-br from-green-400 to-emerald-500',
      icon: <CheckCircle2 size={28} className="text-white" />,
      title: t('networkingPage.probablySafeTitle'),
      subtitle: t('networkingPage.probablySafeSubtitle')
    },
    warning: {
      bg: 'bg-gradient-to-br from-amber-400 to-orange-500',
      icon: <AlertTriangle size={28} className="text-white" />,
      title: t('networkingPage.toVerifyTitle'),
      subtitle: t('networkingPage.toVerifySubtitle')
    },
    danger: {
      bg: 'bg-gradient-to-br from-red-400 to-rose-500',
      icon: <Siren size={28} className="text-white" />,
      title: t('networkingPage.attentionTitle'),
      subtitle: t('networkingPage.attentionSubtitle')
    },
    unknown: {
      bg: 'bg-gradient-to-br from-gray-400 to-gray-500',
      icon: <HelpCircle size={28} className="text-white" />,
      title: t('networkingPage.inAnalysisTitle'),
      subtitle: t('networkingPage.inAnalysisSubtitle')
    }
  };
  
  const config = verdictConfig[verdict];
  
  // Estrai info chiave
  const sourceIP = enrichedDetails?.source_ip || enrichedDetails?.src_ip || incident?.source_ip || incident?.affected_ip;
  const destIP = enrichedDetails?.dest_ip || enrichedDetails?.dst_ip || incident?.dest_ip;
  const destOrg = enrichedDetails?.dst_org || enrichedDetails?.details?.dst_org;
  const isWhitelisted = enrichedDetails?.dest_ip_classification?.ip_intelligence?.is_whitelisted;
  const whitelistProvider = enrichedDetails?.dest_ip_classification?.ip_intelligence?.whitelist_provider;
  
  return (
    <div className="relative overflow-hidden rounded-2xl shadow-lg">
      {/* Sfondo con gradiente */}
      <div className={`${config.bg} p-6 text-white`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-5xl">{config.icon}</span>
              <div>
                <h2 className="text-2xl font-bold">{config.title}</h2>
                <p className="text-white/80 text-sm">{config.subtitle}</p>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-white/60 uppercase tracking-wider">Incidente</div>
            <div className="text-2xl font-mono font-bold">#{incident?.id || incident?.formatted_id}</div>
          </div>
        </div>
      </div>
      
      {/* Info rapide */}
      <div className="bg-white p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-1"><Monitor size={24} /></div>
            <div className="text-xs text-gray-500">Il tuo dispositivo</div>
            <div className="font-mono text-sm font-medium truncate" title={sourceIP}>{sourceIP || '—'}</div>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-1"><Globe size={24} /></div>
            <div className="text-xs text-gray-500">Destinazione</div>
            <div className="font-mono text-sm font-medium truncate" title={destIP}>{destIP || '—'}</div>
            {destOrg && <div className="text-xs text-gray-400 truncate">{destOrg}</div>}
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-1">{isWhitelisted ? <CheckCircle2 size={24} className="text-emerald-500" /> : <HelpCircle size={24} />}</div>
            <div className="text-xs text-gray-500">Reputazione dest.</div>
            <div className="text-sm font-medium">{isWhitelisted ? 'Verificata' : 'Da verificare'}</div>
            {isWhitelisted && whitelistProvider && <div className="text-xs text-green-600 truncate">{whitelistProvider}</div>}
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl mb-1">{priority.icon}</div>
            <div className="text-xs text-gray-500">{t('networkingPage.priorityLabel')}</div>
            <div className="text-sm font-bold">{t(priority.shortLabel)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Detector Scores Visual - mostra i punteggi dei rilevatori in modo grafico
function DetectorScoresVisual({ scores }) {
  const { t } = useTranslation();
  if (!scores) return null;
  
  const detectorInfo = {
    jitter: { label: t('networkingPage.regularity'), icon: <Timer size={14} className="inline" />, description: t('networkingPage.regularityDesc') },
    fft: { label: t('networkingPage.patternLabel'), icon: <BarChart3 size={14} className="inline" />, description: t('networkingPage.patternDesc') },
    entropy: { label: t('networkingPage.randomness'), icon: <Dice5 size={14} className="inline" />, description: t('networkingPage.randomnessDesc') },
    payload: { label: t('networkingPage.contentLabel'), icon: <Package size={14} className="inline" />, description: t('networkingPage.contentDesc') }
  };
  
  const getScoreColor = (score) => {
    if (score >= 0.8) return 'bg-red-500';
    if (score >= 0.6) return 'bg-orange-500';
    if (score >= 0.4) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <Microscope size={16} className="inline" /> {t('networkingPage.aiAnalysisDetail')}
      </h4>
      <div className="grid grid-cols-2 gap-3">
        {Object.entries(scores).map(([key, score]) => {
          const info = detectorInfo[key] || { label: key, icon: <TrendingUp size={14} className="inline" />, description: t('networkingPage.detectorScore') };
          return (
            <div key={key} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium flex items-center gap-1">
                  <span>{info.icon}</span>
                  {info.label}
                </span>
                <span className="text-sm font-bold">{(score * 100).toFixed(0)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${getScoreColor(score)} rounded-full transition-all duration-500`}
                  style={{ width: `${score * 100}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">{info.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Simple Action Buttons - pulsanti azione chiari per utenti non tecnici
function SimpleActionButtons({ incident, onUpdateStatus, currentStatus }) {
  const { t } = useTranslation();
  const [showConfirm, setShowConfirm] = React.useState(null);
  const normalizedCurrentStatus = (currentStatus || '').toLowerCase();
  
  const actions = [
    {
      id: 'IN CORSO',
      matchStatus: 'in-progress',
      label: t('networkingPage.verifyingBtn'),
      description: t('networkingPage.verifyingDesc'),
      icon: <Search size={20} />,
      color: 'bg-blue-600 hover:bg-blue-700',
      mlImpact: false,
      confirm: null
    },
    {
      id: 'CONFERMATO',
      matchStatus: 'confermato',
      label: t('networkingPage.confirmedThreatBtn'),
      description: t('networkingPage.confirmedThreatDesc'),
      icon: <AlertTriangle size={20} />,
      color: 'bg-orange-600 hover:bg-orange-700',
      mlImpact: true,
      confirm: t('networkingPage.confirmThreatMsg')
    },
    {
      id: 'FALSO POSITIVO',
      matchStatus: 'falso positivo',
      label: t('networkingPage.falsePositiveBtn'),
      description: t('networkingPage.falsePositiveDesc'),
      icon: <CheckCircle2 size={20} />,
      color: 'bg-emerald-600 hover:bg-emerald-700',
      mlImpact: true,
      confirm: t('networkingPage.confirmFPMsg')
    },
    {
      id: 'CHIUSO',
      matchStatus: 'chiuso',
      label: t('networkingPage.closeIncidentBtn'),
      description: t('networkingPage.closeIncidentDesc'),
      icon: <Circle size={20} className="fill-gray-400 text-gray-400" />,
      color: 'bg-gray-600 hover:bg-gray-700',
      mlImpact: false,
      confirm: null
    }
  ];
  
  const handleAction = (action) => {
    if (action.confirm) {
      setShowConfirm(action);
    } else {
      onUpdateStatus(action.id);
    }
  };
  
  const confirmAction = () => {
    if (showConfirm) {
      onUpdateStatus(showConfirm.id);
      setShowConfirm(null);
    }
  };
  
  // Determina se l'incidente è già stato gestito
  const isResolved = ['confermato', 'falso positivo', 'chiuso'].includes(normalizedCurrentStatus);
  const resolvedMessages = {
    'confermato': { icon: <AlertTriangle size={14} className="text-orange-500" />, bg: 'bg-orange-50 text-orange-700 border border-orange-200', text: t('networkingPage.alreadyConfirmedThreat') },
    'falso positivo': { icon: <CheckCircle2 size={14} className="text-emerald-500" />, bg: 'bg-emerald-50 text-emerald-700 border border-emerald-200', text: t('networkingPage.alreadyFP') },
    'chiuso': { icon: <Circle size={14} className="fill-gray-500 text-gray-500" />, bg: 'bg-gray-50 text-gray-700 border border-gray-200', text: t('networkingPage.alreadyClosed') }
  };
  const resolvedInfo = resolvedMessages[normalizedCurrentStatus];
  
  return (
    <div className="space-y-3">
      <h4 className="font-semibold text-gray-700 flex items-center gap-2">
        <Target size={14} className="inline" /> {t('networkingPage.whatToDoQuestion')}
      </h4>
      
      {/* Nota esplicativa sull'impatto ML */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
        <strong><Lightbulb size={12} className="inline" /> Nota:</strong>{' '}<span dangerouslySetInnerHTML={{ __html: t('networkingPage.mlNote') }} />
      </div>
      
      {isResolved && resolvedInfo && (
        <div className={`p-3 rounded-lg text-sm font-medium flex items-center gap-2 ${resolvedInfo.bg}`}>
          <span>{resolvedInfo.icon}</span>
          {t('networkingPage.incidentHasBeen')} {resolvedInfo.text}{t('networkingPage.canStillModify')}
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-3">
        {actions.map((action) => {
          const isActive = normalizedCurrentStatus === action.matchStatus;
          return (
            <button
              key={action.id}
              onClick={() => handleAction(action)}
              disabled={isActive}
              className={`p-4 rounded-xl text-white text-center transition-all transform hover:scale-105 ${action.color} ${isActive ? 'opacity-50 cursor-not-allowed ring-2 ring-offset-2 ring-gray-400' : ''}`}
            >
              <span className="text-2xl block mb-1">{action.icon}</span>
              <span className="font-medium text-sm block">{action.label}</span>
              <span className="text-xs text-white/70 block mt-1">{action.description}</span>
              {action.mlImpact && <span className="text-xs block mt-1 bg-white/20 rounded px-1.5 py-0.5"><Bot size={12} className="inline" /> {t('networkingPage.mlImpact')}</span>}
              {isActive && <span className="text-xs block mt-1 text-white/90 font-bold"><Check size={12} className="inline" /> {t('networkingPage.currentStatusLabel')}</span>}
            </button>
          );
        })}
      </div>
      
      {/* Modal di conferma */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
            <h3 className="text-lg font-bold mb-3">{t('networkingPage.confirmActionTitle')}</h3>
            <p className="text-gray-600 mb-4">{showConfirm.confirm}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                {t('networkingPage.cancelBtn')}
              </button>
              <button
                onClick={confirmAction}
                className={`flex-1 px-4 py-2 rounded-lg text-white ${showConfirm.color}`}
              >
                {t('networkingPage.confirmBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Connection Details Panel - pannello espandibile con dettagli tecnici
function ConnectionDetailsPanel({ details }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  
  if (!details) return null;
  
  const { src_port_info, dst_port_info, protocol_info, traffic, dst_asn_name, dst_org, dst_isp, detection_method, mb_size } = details;
  
  // Se non ci sono dati significativi, mostra messaggio
  const hasData = src_port_info || dst_port_info || protocol_info || traffic || dst_asn_name;
  
  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <span className="font-medium text-sm text-gray-700 flex items-center gap-2">
          <Plug size={14} className="inline" /> {t('networkingPage.connectionDetails')}
          {mb_size && <span className="text-xs text-gray-500 font-normal">({mb_size.toFixed(2)} MB)</span>}
        </span>
        <svg 
          className={`w-4 h-4 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-200">
          {!hasData ? (
            <div className="text-sm text-gray-400 italic mt-3">{t('networkingPage.connectionDetailsNA')}</div>
          ) : (
            <>
              {/* Info Destinazione ASN */}
              {(dst_asn_name || dst_org || dst_isp) && (
                <div className="mt-3 bg-white rounded p-3 border border-gray-100">
                  <div className="text-xs text-gray-500 mb-2">{t('networkingPage.destInfo')}</div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    {dst_asn_name && (
                      <div>
                        <span className="text-gray-500 text-xs">ASN:</span>
                        <div className="font-medium">{dst_asn_name}</div>
                      </div>
                    )}
                    {dst_org && (
                      <div>
                        <span className="text-gray-500 text-xs">Organizzazione:</span>
                        <div className="font-medium">{dst_org}</div>
                      </div>
                    )}
                    {dst_isp && (
                      <div>
                        <span className="text-gray-500 text-xs">ISP:</span>
                        <div className="font-medium">{dst_isp}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Detection Method */}
              {detection_method && (
                <div className="mt-3">
                  <span className="text-xs text-gray-500">{t('networkingPage.detectionMethod')} </span>
                  <span className="inline-flex px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-medium">
                    {detection_method.replace(/_/g, ' ').toUpperCase()}
                  </span>
                </div>
              )}
              
              {/* Porte */}
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <div className="text-xs text-gray-500 mb-1">{t('networkingPage.sourcePort')}</div>
                  <div className="text-sm font-medium">
                    <span className="font-mono">{src_port_info?.port || '—'}</span>
                    {src_port_info?.name && <span className="text-gray-500 ml-2">({src_port_info.name})</span>}
                  </div>
                  {src_port_info?.description && (
                    <div className="text-xs text-gray-500 mt-0.5">{src_port_info.description}</div>
                  )}
                  {src_port_info?.is_suspicious && (
                    <span className="inline-flex mt-1 px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] rounded"><AlertTriangle size={10} className="inline" /> Sospetta</span>
                  )}
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">{t('networkingPage.destPort')}</div>
                  <div className="text-sm font-medium">
                    <span className="font-mono">{dst_port_info?.port || '—'}</span>
                    {dst_port_info?.name && <span className="text-gray-500 ml-2">({dst_port_info.name})</span>}
                  </div>
                  {dst_port_info?.description && (
                    <div className="text-xs text-gray-500 mt-0.5">{dst_port_info.description}</div>
                  )}
                  {dst_port_info?.is_well_known && (
                    <span className="inline-flex mt-1 px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] rounded"><Check size={10} className="inline" /> Porta nota</span>
                  )}
                </div>
              </div>
              
              {/* Protocollo */}
              {protocol_info && (
                <div>
                  <div className="text-xs text-gray-500 mb-1">{t('networkingPage.protocolLabel')}</div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-sm font-medium">{protocol_info.name}</span>
                    {protocol_info.full_name && <span className="text-sm text-gray-600">{protocol_info.full_name}</span>}
                    {protocol_info.type && (
                      <span className="text-xs text-gray-500">({protocol_info.type})</span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Traffico */}
              {traffic && (
                <div>
                  <div className="text-xs text-gray-500 mb-2">{t('networkingPage.trafficLabel')}</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded p-2 border border-gray-100">
                      <div className="text-xs text-gray-500">{t('networkingPage.sent')}</div>
                      <div className="text-sm font-semibold text-blue-600">{traffic.bytes_sent_human || '—'}</div>
                    </div>
                    <div className="bg-white rounded p-2 border border-gray-100">
                      <div className="text-xs text-gray-500">{t('networkingPage.received')}</div>
                      <div className="text-sm font-semibold text-green-600">{traffic.bytes_received_human || '—'}</div>
                    </div>
                    <div className="bg-white rounded p-2 border border-gray-100">
                      <div className="text-xs text-gray-500">{t('networkingPage.duration')}</div>
                      <div className="text-sm font-semibold text-gray-700">{traffic.duration_human || '—'}</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Incident detail modal - Versione 3.0 User-Friendly
function IncidentDetailModal({ incident, tenantId, onClose, onUpdateStatus, onAddComment }) {
  const { t } = useTranslation();
  // State per tab attiva (modalità semplice vs avanzata)
  const [viewMode, setViewMode] = useState('simple'); // 'simple' | 'advanced'
  const [showHelp, setShowHelp] = useState(false);
  
  // Ensure the modal always shows rich timeline/events/actions even when
  // the incident object comes from a different part of the UI (mock or
  // real API) and doesn't already include them.
  const buildDetailsIfMissing = (inc) => {
    if (!inc) return inc;
    const copy = { ...inc };
    const desc = (copy.description || copy.threat_type || '').toString();
    const threat = (copy.threat_type || '').toLowerCase();

    if (!Array.isArray(copy.events) || copy.events.length === 0) {
      const events = [];
      const recommended_actions = [];

      if (threat.includes('data_exfiltration') || /upload/i.test(desc)) {
        const sizeMatch = desc.match(/([0-9]+\.[0-9]+|[0-9]+)\s*MB/i);
        const size = sizeMatch ? `${sizeMatch[1]} MB` : null;
        const destMatch = desc.match(/to\s+([0-9]{1,3}(?:\.[0-9]{1,3}){3})/i) || desc.match(/to:\s*([0-9]{1,3}(?:\.[0-9]{1,3}){3})/i);
        const destIp = destMatch ? destMatch[1] : null;

        events.push({ type: 'ML Anomaly', timestamp: copy.opened_at || copy.created_at || new Date().toISOString(), description: size ? `Rilevato upload di ${size}` : 'Rilevato upload sospetto' });
        events.push({ type: 'Correlazione', timestamp: copy.opened_at || copy.created_at || new Date().toISOString(), description: destIp ? `Traffico verso IP esterno non autorizzato: ${destIp}` : 'Traffico verso IP esterno non autorizzato' });
        if (destIp) events.push({ type: 'IoC Match', timestamp: copy.opened_at || copy.created_at || new Date().toISOString(), description: 'IP destinazione presente in threat feed', ioc_match: destIp });

        if (destIp) recommended_actions.push(`Bloccare l'IP di destinazione ${destIp} sul firewall`);
        recommended_actions.push(`Verificare quali dati sono stati esfiltrati da ${copy.affected_ip || 'host coinvolto'}`);
        recommended_actions.push("Resettare le credenziali dell'utente coinvolto");
        recommended_actions.push('Avviare analisi forense completa');
      } else if (threat.includes('c2') || threat.includes('beacon')) {
        const countMatch = desc.match(/(\d+)\s*volte|(\d+) attempts|contattato\s+[0-9.]+:\d+\s*(\d+)?/i);
        const count = countMatch ? (countMatch[1] || countMatch[2] || countMatch[3]) : null;
        const peerMatch = desc.match(/(\d{1,3}(?:\.\d{1,3}){3}):\d+/);
        const peer = peerMatch ? peerMatch[1] : null;

        events.push({ type: 'ML Anomaly', timestamp: copy.opened_at || copy.created_at || new Date().toISOString(), description: count ? `Rilevati ${count} contatti verso C2` : 'Rilevato pattern di connessione ripetitivo' });
        if (peer) events.push({ type: 'IoC Match', timestamp: copy.opened_at || copy.created_at || new Date().toISOString(), description: 'IP destinazione presente in threat feed', ioc_match: peer });
        if (peer) events.push({ type: 'Peer', timestamp: copy.opened_at || copy.created_at || new Date().toISOString(), description: `Peer: ${peer}` });

        recommended_actions.push("Isolare immediatamente l'host compromesso");
        recommended_actions.push('Bloccare comunicazioni verso i C2 noti e peer rilevati');
        recommended_actions.push('Eseguire analisi forense sulle connessioni in uscita');
      } else if (threat.includes('ssh') || threat.includes('bruteforce')) {
        const attemptsMatch = desc.match(/(\d+)\s*tentativi|ha effettuato\s*(\d+)\s*tentativi/i);
        const attempts = attemptsMatch ? (attemptsMatch[1] || attemptsMatch[2]) : null;
        const attackerMatch = desc.match(/(\d{1,3}(?:\.\d{1,3}){3})/);
        const attacker = attackerMatch ? attackerMatch[1] : copy.affected_ip || null;

        events.push({ type: 'ML Anomaly', timestamp: copy.opened_at || copy.created_at || new Date().toISOString(), description: attempts ? `${attempts} tentativi SSH rilevati` : 'Tentativi SSH multipli rilevati' });
        if (attacker) events.push({ type: 'IoC Match', timestamp: copy.opened_at || copy.created_at || new Date().toISOString(), description: 'IP sorgente presente in threat feed', ioc_match: attacker });
        if (attacker) events.push({ type: 'Attaccante', timestamp: copy.opened_at || copy.created_at || new Date().toISOString(), description: `IP sorgente: ${attacker}` });

        if (attacker) recommended_actions.push(`Bloccare l'IP sorgente ${attacker} sul firewall`);
        recommended_actions.push("Disabilitare l'autenticazione password su SSH e usare chiavi");
        recommended_actions.push('Controllare i log di accesso e cambiare credenziali compromesse');
      } else {
        events.push({ type: 'Rilevamento ML', timestamp: copy.opened_at || copy.created_at || new Date().toISOString(), description: desc || 'Anomalia rilevata dal motore ML' });
        recommended_actions.push('Analizzare i log correlati');
        recommended_actions.push("Valutare eventuale isolamento dell'host");
      }

      copy.events = events;
      copy.recommended_actions = recommended_actions;
      if (!Array.isArray(copy.comments)) copy.comments = [];
    }

    // Ensure recommended_actions exists even if events were present (defensive)
    if (!Array.isArray(copy.recommended_actions)) copy.recommended_actions = [];
    if (!Array.isArray(copy.comments)) copy.comments = [];

    return copy;
  };

  const [incidentState, setIncidentState] = useState(() => buildDetailsIfMissing(incident));
  const [comment, setComment] = useState('');
  // Mappa lo status locale al valore API NetFlow per il dropdown avanzato
  const localToNetflowStatus = (s) => {
    const map = {
      'open': 'open',
      'in-progress': 'IN CORSO',
      'confermato': 'CONFERMATO',
      'falso positivo': 'FALSO POSITIVO',
      'chiuso': 'CHIUSO'
    };
    return map[(s || '').toLowerCase()] || s;
  };
  const [newStatus, setNewStatus] = useState(() => localToNetflowStatus(incident?.status || 'open'));
  const [enrichedDetails, setEnrichedDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);

  // Rebuild local state when prop changes
  useEffect(() => {
    setIncidentState(buildDetailsIfMissing(incident));
    setNewStatus(localToNetflowStatus(incident?.status || 'open'));
  }, [incident]);

  // Fetch dettagli arricchiti dall'API quando si apre il modal
  useEffect(() => {
    const fetchEnrichedDetails = async () => {
      if (!incident?.tenant_id || !incident?.remote_id) return;
      
      setLoadingDetails(true);
      try {
        const url = API_ENDPOINTS.netflowIncidentDetail(incident.tenant_id, incident.remote_id);
        const res = await fetchWithAuth(url);
        if (res && res.ok) {
          const data = await res.json();
          
          // L'API può restituire i dettagli in data.details o direttamente in data
          const details = data.details || data;
          setEnrichedDetails(details);
          setAnalysisData(data.analysis || details?.analysis || null);
          
          // Aggiorna incidentState con dati arricchiti dal server
          const timeline = data.timeline || details?.timeline;
          const recommended = data.recommended_actions || details?.recommended_actions;
          const updatedFields = {};
          
          if (timeline) {
            updatedFields.events = timeline.map(t => ({
              type: t.title || t.type,
              timestamp: t.time || t.timestamp,
              description: t.description
            }));
          }
          if (recommended) {
            updatedFields.recommended_actions = recommended;
          }
          // Aggiorna anche campi extra se presenti nell'enriched detail
          if (details?.ml_confidence !== undefined) updatedFields.ml_confidence = details.ml_confidence;
          if (details?.source_ip_classification) updatedFields.source_ip_classification = details.source_ip_classification;
          if (details?.dest_ip_classification) updatedFields.dest_ip_classification = details.dest_ip_classification;
          
          if (Object.keys(updatedFields).length > 0) {
            setIncidentState(prev => ({ ...prev, ...updatedFields }));
          }
        }
      } catch (err) {
        console.warn('[NetFlow] Failed to fetch enriched details:', err);
      } finally {
        setLoadingDetails(false);
      }
    };
    
    fetchEnrichedDetails();
  }, [incident?.tenant_id, incident?.remote_id]);

  if (!incident) return null;

  const handleSubmitComment = () => {
    if (comment.trim()) {
      onAddComment(incidentState.id, comment);
      setComment('');
      // update local comments
      setIncidentState(prev => ({ ...prev, comments: [...(prev.comments || []), { user: 'current_user', timestamp: new Date().toLocaleTimeString(), text: comment }] }));
    }
  };

  const handleStatusChange = (statusValue) => {
    onUpdateStatus(incidentState.id, statusValue);
    // Normalizza lo status per il display locale
    // Normalizza lo status per il display locale
    const upper = (statusValue || '').toUpperCase();
    const statusMap = {
      'FALSO POSITIVO': 'falso positivo',
      'CONFERMATO': 'confermato',
      'CHIUSO': 'chiuso',
      'IN CORSO': 'in-progress'
    };
    const displayStatus = statusMap[upper] || statusValue;
    setIncidentState(prev => ({ ...prev, status: displayStatus }));
  };

  // Format date in Italian friendly format
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-50 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        
        {/* Header compatto con toggle vista */}
        <div className="sticky top-0 bg-white border-b shadow-sm z-10">
          {/* Status bar prominente */}
          <div className={`px-6 py-2 text-center text-xs font-bold uppercase tracking-wider ${
            incidentState.status === 'falso positivo' ? 'bg-emerald-100 text-emerald-800 border-b border-emerald-200' :
            incidentState.status === 'confermato' ? 'bg-orange-100 text-orange-800 border-b border-orange-200' :
            incidentState.status === 'open' ? 'bg-red-100 text-red-800 border-b border-red-200' :
            incidentState.status === 'in-progress' ? 'bg-blue-100 text-blue-800 border-b border-blue-200' :
            incidentState.status === 'chiuso' ? 'bg-gray-100 text-gray-800 border-b border-gray-200' :
            'bg-gray-100 text-gray-800 border-b border-gray-200'
          }`}>
            {t('networkingPage.incidentStatusLabel')} {
              incidentState.status === 'falso positivo' ? <><CheckCircle2 size={14} className="inline" /> {t('networkingPage.fpClassified')}</> :
              incidentState.status === 'confermato' ? <><AlertTriangle size={14} className="inline" /> {t('networkingPage.confirmedClassified')}</> :
              incidentState.status === 'open' ? <><Circle size={14} className="inline fill-red-500 text-red-500" /> {t('networkingPage.openStatus')}</> :
              incidentState.status === 'in-progress' ? <><Circle size={14} className="inline fill-blue-500 text-blue-500" /> {t('networkingPage.inProgressStatus')}</> :
              incidentState.status === 'chiuso' ? <><Circle size={14} className="inline fill-gray-500 text-gray-500" /> {t('networkingPage.closedStatus')}</> :
              (incidentState.status || 'SCONOSCIUTO').toUpperCase()
            }
          </div>
          <div className="px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <PriorityBadge severity={incidentState.severity} mlScore={incidentState.ml_score} size="lg" />
                <StatusBadge status={incidentState.status} size="lg" />
              </div>
              <span className="text-gray-400">|</span>
              <span className="text-sm text-gray-500">ID: {incidentState.id || incidentState.formatted_id}</span>
            </div>
          
          <div className="flex items-center gap-3">
            {/* Toggle Vista Semplice/Avanzata */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('simple')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  viewMode === 'simple' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <><User size={14} className="inline" /> {t('networkingPage.simpleView')}</>
              </button>
              <button
                onClick={() => setViewMode('advanced')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  viewMode === 'advanced' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <><Wrench size={14} className="inline" /> {t('networkingPage.technicalView')}</>
              </button>
            </div>
            
            {/* Help Button */}
            <button 
              onClick={() => setShowHelp(!showHelp)}
              className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-full transition-colors"
              title={t('networkingPage.helpTitle')}
            >
              <span className="text-lg"><HelpCircle size={18} className="inline" /></span>
            </button>
            
            {/* Close Button */}
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          </div>
        </div>
        {showHelp && (
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
            <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <Lightbulb size={16} className="inline" /> {t('networkingPage.helpTitle')}
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm text-blue-700">
              <div>
                <p><strong><Circle size={10} className="inline fill-green-500 text-green-500" /> {t('networkingPage.probablySafeTitle')}:</strong> {t('networkingPage.helpSafe')}</p>
                <p><strong><Circle size={10} className="inline fill-amber-500 text-amber-500" /> {t('networkingPage.toVerifyTitle')}:</strong> {t('networkingPage.helpCheck')}</p>
              </div>
              <div>
                <p><strong><Circle size={10} className="inline fill-red-500 text-red-500" /> {t('networkingPage.attentionTitle')}:</strong> {t('networkingPage.helpAttention')}</p>
                <p><strong>{t('networkingPage.simpleView')}:</strong> {t('networkingPage.helpSimple')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Contenuto scrollabile */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Loading indicator */}
          {loadingDetails && (
            <div className="text-center py-8">
              <div className="inline-flex items-center gap-3 text-gray-500 bg-white px-6 py-4 rounded-xl shadow-sm">
                <svg className="animate-spin h-6 w-6 text-blue-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="text-lg">{t('networkingPage.analyzingIncident')}</span>
              </div>
            </div>
          )}

          {/* ========== VISTA SEMPLICE ========== */}
          {viewMode === 'simple' && !loadingDetails && (
            <>
              {/* Quick Summary Card - sempre in cima */}
              <QuickSummaryCard 
                incident={incidentState}
                analysis={analysisData}
                enrichedDetails={enrichedDetails}
              />
              
              {/* Raccomandazione AI - il pannello più importante */}
              <MLRecommendationPanel 
                analysis={analysisData}
                details={enrichedDetails}
              />
              
              {/* Spiegazione della minaccia in linguaggio semplice */}
              <ThreatInfoPanel 
                threatInfo={analysisData?.threat_info}
                threatType={incidentState.threat_type}
                severity={incidentState.severity}
                mlScore={incidentState.ml_score}
              />
              
              {/* Pulsanti azione semplificati */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <SimpleActionButtons 
                  incident={incidentState}
                  onUpdateStatus={handleStatusChange}
                  currentStatus={incidentState.status}
                />
              </div>

              {/* Info dispositivi coinvolti - versione semplificata */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <MapPin size={16} className="inline" /> {t('networkingPage.devicesInvolved')}
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {/* Dispositivo interno */}
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-3xl"><Monitor size={32} /></span>
                      <div>
                        <div className="text-xs text-blue-600 font-medium uppercase">{t('networkingPage.yourDevice')}</div>
                        <EducationalTooltip term="internal_ip">
                          <div className="font-mono text-lg">{enrichedDetails?.src_ip || incidentState.source_ip || incidentState.affected_ip || '—'}</div>
                        </EducationalTooltip>
                      </div>
                    </div>
                    <p className="text-sm text-blue-700">
                      {enrichedDetails?.source_ip_classification?.ip_type_info?.name || 'Computer nella tua rete'}
                    </p>
                  </div>
                  
                  {/* Destinazione esterna */}
                  <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-3xl">
                        {enrichedDetails?.dest_ip_classification?.ip_intelligence?.is_whitelisted ? <CheckCircle2 size={32} className="text-emerald-500" /> : <Globe size={32} />}
                      </span>
                      <div>
                        <div className="text-xs text-purple-600 font-medium uppercase">Destinazione</div>
                        <EducationalTooltip term="external_ip">
                          <div className="font-mono text-lg">{enrichedDetails?.dst_ip || incidentState.dest_ip || '—'}</div>
                        </EducationalTooltip>
                      </div>
                    </div>
                    <p className="text-sm text-purple-700">
                      {enrichedDetails?.dst_org || enrichedDetails?.dest_ip_classification?.ip_type_info?.name || 'Server esterno'}
                    </p>
                    {enrichedDetails?.dest_ip_classification?.ip_intelligence?.is_whitelisted && (
                      <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                        <Check size={12} className="inline" /> Servizio verificato ({enrichedDetails?.dest_ip_classification?.ip_intelligence?.whitelist_provider})
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Possibile uso porta - spiegazione semplice */}
                {enrichedDetails?.dst_port_info?.possible_use && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm">
                      <Lightbulb size={14} className="inline" />
                      <span className="text-gray-600">
                        <strong>Possibile spiegazione:</strong> {enrichedDetails.dst_port_info.possible_use.description} 
                        ({enrichedDetails.dst_port_info.possible_use.service})
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Azioni consigliate - versione semplificata */}
              <div className="bg-amber-50 rounded-xl border border-amber-200 p-5 shadow-sm">
                <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                  <ClipboardList size={16} className="inline" /> {t('networkingPage.suggestedSteps')}
                </h4>
                <ol className="space-y-3">
                  {incidentState.recommended_actions.slice(0, 4).map((action, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center text-sm font-bold">
                        {idx + 1}
                      </span>
                      <span className="text-gray-700">{action}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Sezione commenti - semplificata */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <MessageCircle size={16} className="inline" /> {t('networkingPage.notesAndComments')}
                </h4>
                {incidentState.comments.length === 0 && (
                  <p className="text-gray-400 text-sm mb-3">{t('networkingPage.noComments')}</p>
                )}
                {incidentState.comments.map((c, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-3 mb-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-700 text-sm">{c.user}</span>
                      <span className="text-xs text-gray-400">{c.timestamp}</span>
                    </div>
                    <p className="text-gray-600 text-sm">{c.text}</p>
                  </div>
                ))}
                <div className="flex gap-2 mt-3">
                  <input
                    type="text"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Scrivi una nota..."
                    className="flex-1 border border-gray-200 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && handleSubmitComment()}
                  />
                  <button 
                    onClick={handleSubmitComment}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    Aggiungi
                  </button>
                </div>
              </div>
            </>
          )}

          {/* ========== VISTA AVANZATA (TECNICA) ========== */}
          {viewMode === 'advanced' && !loadingDetails && (
            <>
              {/* Info principale - versione tecnica */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{t('networkingPage.incidentId')}</div>
                    <div className="text-sm font-semibold font-mono">{enrichedDetails?.formatted_id || incidentState.id || incidentState.formatted_id || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Stato</div>
                    <StatusBadge status={incidentState.status} size="lg" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Severity (dato grezzo API)</div>
                    <SeverityBadge severity={enrichedDetails?.severity || incidentState.severity} />
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{t('networkingPage.threatTypeLabel')}</div>
                    <div className="text-sm font-semibold font-mono">{enrichedDetails?.threat_type || incidentState.threat_type || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{t('networkingPage.mlScoreLabel')}</div>
                    <div className="text-sm font-semibold text-red-600">{(enrichedDetails?.ml_score || incidentState.ml_score || 0).toFixed(4)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{t('networkingPage.mlConfidenceLabel')}</div>
                    <div className="text-sm font-semibold text-blue-600">{((enrichedDetails?.ml_confidence || incidentState.ml_confidence || 0) * 100).toFixed(1)}%</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{t('networkingPage.detectedAt')}</div>
                    <div className="text-sm">{formatDate(enrichedDetails?.created_at || incidentState.opened_at || incidentState.created_at)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{t('networkingPage.closedAt')}</div>
                    <div className="text-sm">{formatDate(enrichedDetails?.closed_at || incidentState.closed_at)}</div>
                  </div>
                </div>
                {/* Descrizione */}
                {(enrichedDetails?.description || incidentState.description) && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="text-xs text-gray-500 mb-1">Descrizione</div>
                    <div className="text-sm text-gray-700">{enrichedDetails?.description || incidentState.description}</div>
                  </div>
                )}
                {/* Source */}
                {(enrichedDetails?.source || incidentState.source) && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-500 mb-1">Sorgente Rilevamento</div>
                    <SourceBadge source={enrichedDetails?.source || incidentState.source} />
                  </div>
                )}
              </div>

              {/* Detector Scores */}
              {enrichedDetails?.detector_scores && (
                <DetectorScoresVisual scores={enrichedDetails.detector_scores} />
              )}

              {/* Sezione IP Classification - v2.0 */}
              <div className="bg-white border rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold mb-4 flex items-center gap-2 text-gray-700">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  Classificazione IP
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-2">{t('networkingPage.sourceIPLabel')}</div>
                    <div className="font-mono text-sm mb-2">{enrichedDetails?.src_ip || incidentState.source_ip || incidentState.affected_ip || '—'}</div>
                    <div className="space-y-2">
                      <IPClassificationBadge classification={
                        enrichedDetails?.source_ip_classification || 
                        incidentState.source_ip_classification
                      } />
                      <div className="mt-2">
                        <IPIntelligenceLookup ip={enrichedDetails?.src_ip || incidentState.source_ip || incidentState.affected_ip} />
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-2">{t('networkingPage.destIPLabel')}</div>
                    <div className="font-mono text-sm mb-2">{enrichedDetails?.dst_ip || incidentState.dest_ip || '—'}</div>
                    <div className="space-y-2">
                      <IPClassificationBadge classification={
                        enrichedDetails?.dest_ip_classification ||
                        incidentState.dest_ip_classification
                      } />
                      <div className="mt-2">
                        <IPIntelligenceLookup ip={enrichedDetails?.dst_ip || incidentState.dest_ip} />
                      </div>
                    </div>
                  </div>
                </div>
                {/* Repeat Offender Alert */}
                {(enrichedDetails?.ip_history?.is_repeat_offender || incidentState.is_repeat_offender) && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <span className="font-semibold text-red-700">Repeat Offender</span>
                      <span className="text-red-600 ml-2">
                        {t('networkingPage.srcIPInvolved')} {enrichedDetails?.ip_history?.src_ip_incidents_30d || incidentState.repeat_offender_count || 0} incidenti (ultimi 30gg)
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Sezione MITRE ATT&CK */}
              {(enrichedDetails?.mitre_techniques || enrichedDetails?.threat_context?.mitre_technique || incidentState.mitre_technique) && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-5 shadow-sm">
                  <h3 className="font-semibold mb-3 flex items-center gap-2 text-purple-900">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    MITRE ATT&CK Framework
                  </h3>
                  <div className="flex flex-wrap items-center gap-3">
                    {(enrichedDetails?.mitre_techniques || [enrichedDetails?.threat_context?.mitre_technique || incidentState.mitre_technique]).filter(Boolean).map((tech, idx) => (
                      <MitreBadge 
                        key={idx}
                        technique={tech}
                        name={enrichedDetails?.threat_context?.mitre_name || incidentState.mitre_technique_name}
                      />
                    ))}
                    {(enrichedDetails?.threat_context?.attack_category || incidentState.mitre_tactic) && (
                      <span className="text-sm text-purple-700 bg-purple-100 px-2 py-1 rounded">
                        Categoria: {enrichedDetails?.threat_context?.attack_category || incidentState.mitre_tactic}
                      </span>
                    )}
                  </div>
                  {enrichedDetails?.threat_context?.impact && (
                    <p className="mt-3 text-sm text-purple-800">
                      <strong>Impatto:</strong> {enrichedDetails.threat_context.impact}
                    </p>
                  )}
                  {enrichedDetails?.threat_context?.false_positive_indicators && (
                    <div className="mt-3">
                      <p className="text-xs text-purple-600 mb-1">Possibili Falsi Positivi:</p>
                      <ul className="text-xs text-purple-700 list-disc list-inside">
                        {enrichedDetails.threat_context.false_positive_indicators.map((fp, i) => (
                          <li key={i}>{fp}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Evidence Details */}
              {enrichedDetails?.evidence && (
                <div className="bg-white border rounded-xl p-5 shadow-sm">
                  <h3 className="font-semibold mb-4 flex items-center gap-2 text-gray-700">
                    <Microscope size={16} className="inline" /> Evidence Raw Data
                  </h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="bg-gray-50 rounded p-3">
                      <div className="text-xs text-gray-500">Connection Count</div>
                      <div className="font-mono font-semibold">{enrichedDetails.evidence.connection_count || '—'}</div>
                    </div>
                    <div className="bg-gray-50 rounded p-3">
                      <div className="text-xs text-gray-500">Avg Interval</div>
                      <div className="font-mono font-semibold">{enrichedDetails.evidence.avg_interval_seconds?.toFixed(1) || '—'}s</div>
                    </div>
                    <div className="bg-gray-50 rounded p-3">
                      <div className="text-xs text-gray-500">Coefficient of Variation</div>
                      <div className="font-mono font-semibold">{((enrichedDetails.evidence.coefficient_of_variation || 0) * 100).toFixed(2)}%</div>
                    </div>
                    <div className="bg-gray-50 rounded p-3">
                      <div className="text-xs text-gray-500">Total Bytes</div>
                      <div className="font-mono font-semibold">{enrichedDetails.evidence.total_bytes?.toLocaleString() || '—'}</div>
                    </div>
                    <div className="bg-gray-50 rounded p-3">
                      <div className="text-xs text-gray-500">FFT Peak Ratio</div>
                      <div className="font-mono font-semibold">{enrichedDetails.evidence.fft_peak_ratio?.toFixed(3) || '—'}</div>
                    </div>
                    <div className="bg-gray-50 rounded p-3">
                      <div className="text-xs text-gray-500">Is Beacon</div>
                      <div className="font-mono font-semibold">{enrichedDetails.evidence.is_beacon ? <><Check size={12} className="inline" /> Yes</> : <><X size={12} className="inline" /> No</>}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Sezione Baseline */}
              <div className="bg-white border rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold mb-3 flex items-center gap-2 text-gray-700">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                  Analisi Baseline
                </h3>
                <BaselineBadge 
                  baseline={enrichedDetails?.baseline || enrichedDetails?.details?.baseline || incidentState.baseline_deviation || incidentState.baseline}
                />
              </div>

              {/* Connection Details */}
              <ConnectionDetailsPanel 
                details={{
                  src_port_info: enrichedDetails?.src_port_info || incidentState.src_port_info,
                  dst_port_info: enrichedDetails?.dst_port_info || incidentState.dst_port_info,
                  protocol_info: enrichedDetails?.protocol_info || incidentState.protocol_info,
                  traffic: enrichedDetails?.traffic || incidentState.traffic,
                  dst_asn_name: enrichedDetails?.dst_asn_name,
                  dst_org: enrichedDetails?.dst_org,
                  dst_isp: enrichedDetails?.dst_isp,
                  detection_method: enrichedDetails?.detection_method,
                  mb_size: enrichedDetails?.mb_size
                }}
              />

              {/* Timeline eventi */}
              <div className="bg-gray-50 rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  {t('networkingPage.timelineEvents')}
                </h3>
                <div className="space-y-3">
                  {incidentState.events.map((evt, idx) => (
                    <div key={idx} className="flex gap-3 items-start">
                      <div className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-2"></div>
                      <div className="flex-1 bg-white rounded-lg p-3 text-sm shadow-sm">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-900">{evt.type}</span>
                          <span className="text-xs text-gray-500">{evt.timestamp}</span>
                        </div>
                        <div className="text-gray-600 text-xs">{evt.description}</div>
                        {evt.ioc_match && (
                          <div className="mt-2 text-xs bg-red-50 px-2 py-1 rounded text-red-800">
                            <strong>IoC Match:</strong> {evt.ioc_match}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Azioni consigliate */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold mb-3 flex items-center gap-2 text-amber-900">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  {t('networkingPage.recommendedActions')}
                </h3>
                <ul className="space-y-2">
                  {incidentState.recommended_actions.map((action, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-800">{action}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Gestione stato - versione tecnica */}
              <div className="bg-white border rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  {t('networkingPage.updateStatus')}
                  <StatusBadge status={incidentState.status} />
                </h3>
                <div className="flex gap-3 items-center">
                  <select 
                    value={newStatus} 
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="open">{t('networkingPage.optionOpen')}</option>
                    <option value="IN CORSO">{t('networkingPage.optionInProgress')}</option>
                    <option value="CONFERMATO">{t('networkingPage.optionConfirmed')}</option>
                    <option value="FALSO POSITIVO">{t('networkingPage.optionFP')}</option>
                    <option value="CHIUSO">{t('networkingPage.optionClosed')}</option>
                  </select>
                  <button 
                    onClick={() => handleStatusChange(newStatus)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    Aggiorna
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  <><Zap size={12} className="inline" /> {t('networkingPage.statusWillBeSent')} </><code className="bg-gray-100 px-1 rounded">{newStatus}</code>
                </p>
              </div>

              {/* Raw Data Debug - collapsible */}
              {enrichedDetails && (
                <details className="bg-gray-50 border border-gray-200 rounded-xl shadow-sm">
                  <summary className="px-5 py-3 cursor-pointer text-sm font-semibold text-gray-600 hover:text-gray-800">
                    <><Search size={14} className="inline" /> Raw JSON Data (Debug)</>
                  </summary>
                  <div className="px-5 pb-4">
                    <pre className="bg-gray-900 text-green-400 rounded-lg p-4 text-xs overflow-x-auto max-h-80 overflow-y-auto">
                      {JSON.stringify(enrichedDetails, null, 2)}
                    </pre>
                    {analysisData && (
                      <>
                        <p className="text-xs text-gray-500 mt-3 mb-1 font-semibold">Analysis Data:</p>
                        <pre className="bg-gray-900 text-blue-400 rounded-lg p-4 text-xs overflow-x-auto max-h-60 overflow-y-auto">
                          {JSON.stringify(analysisData, null, 2)}
                        </pre>
                      </>
                    )}
                  </div>
                </details>
              )}

              {/* Commenti */}
              <div className="bg-white border rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold mb-3">{t('networkingPage.commentsAndLog')}</h3>
                <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                  {incidentState.comments.map((c, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-3 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900">{c.user}</span>
                        <span className="text-xs text-gray-500">{c.timestamp}</span>
                      </div>
                      <p className="text-gray-700">{c.text}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t('networkingPage.addComment')}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && handleSubmitComment()}
                  />
                  <button 
                    onClick={handleSubmitComment}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
                  >
                    {t('networkingPage.sendBtn')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
